import { defineStore } from 'pinia';
import { convertKeysToCamelCase, redactSensitiveFields } from '../plugins/GlobalsPlugin';
import { hostTargetOrigin } from '../utils/embedOrigins';
// Default export too: store actions have no `this.$t()`, so user-facing strings raised from here
// (the WS-CONNECTION-ERROR payloads below, rendered by MessengerWindow) resolve via i18n.global.t().
import i18n, { setLocale, primaryLanguage, DEFAULT_LOCALE } from '../i18n';

let _socket = null;
let _visibilityHandlerAttached = false;
// True once the socket has opened at least once this session. Distinguishes the FIRST connect (mount
// already loads history) from a RE-connect (the panel may have missed the live stream of an in-flight
// or just-finished Rey turn and must reconcile from durable history). See _onSocketOpen.
// NOTE: this is a MODULE-level flag, so a hard navigation (switching screens) that reloads the bundle
// resets it to false — making the first post-reload connect look like a brand-new session and skip the
// reconcile. That is exactly the switch-screens-mid-answer case this fix targets, so _onSocketOpen also
// consults a sessionStorage marker (WS_CONNECTED_BEFORE_KEY), which survives a same-tab reload.
const WS_CONNECTED_BEFORE_KEY = 'fusion_ws_connected_before';
let _hasConnectedBefore = false;
// The in-flight EnsureWelcomeGate settings read, so two callers racing on a cold open share one
// request instead of issuing two. FusionBody starts the resolve in its own created(); the Rey
// panel's landing decision awaits the same answer ~500ms later and would otherwise re-fetch
// whenever that first read is still outstanding. Module-private for the same reason
// _hasConnectedBefore is — it is a coordination detail, not state a component should read.
let _welcomeGateInFlight = null;

// Maps FusionInitType.InitSystem values to analytics platform strings (WS-6).
// One-to-one with FusionSystemType; unrecognised values fall through to null.
const PLATFORM_MAP = {
    'ERA':           'ERA',
    'FOCUS':         'FOCUS',
    'ENHANCEDFOCUS': 'FOCUS',
    'REVERSERISK':   'ReverseRisk',
    'AUTOVISION':    'AutoVision',
    'RECONVISION':   'ReconVision',
};

// Rey session lifetime constants (WS-3)
const REY_SESSION_TIMEOUT_MS     = 3 * 60 * 60 * 1000;  // 3 hours — idle timeout for a Rey session
const FRESH_SESSION_THRESHOLD_MS = 5 * 60 * 1000;       // 5 minutes — window in which a sibling iframe's session is considered live and adoptable

// localStorage key for the synchronous "recently cleanly-ended" marker. Used by
// _recoverAbandonedSession to skip sessions that emitted user_closed via the pagehide
// keepalive path — IDB clearSessionState may not commit before the browser kills the
// page, so we need a synchronous, durable marker as a second line of defence against
// duplicate abandoned-recovery emissions.
const REY_ENDED_MARKER_KEY       = 'rey_recently_ended';
const REY_ENDED_MARKER_TTL_MS    = 60 * 60 * 1000;      // 1 hour — markers older than this are pruned on read

// Browser-local calendar date as YYYY-MM-DD. Used as the per-user "welcome shown" day key.
// Local (not UTC) so "today" matches the clock on the user's device. Built from date parts
// rather than toISOString() so no timezone shift is applied.
function todayLocalDateString() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export const useMainStore = defineStore('main', {
  state: () => ({
    token: null,
    socket_connected: false,
    socket_connecting: false,
    socket_try_again: false,
    socket_retries: 0,
    thread_context: null,
    user_settings: null,
    // ReyWelcome once-per-day gate. Resolved once per session from the user-scoped settings
    // service (Ux.WelcomeLastShownDate) so the "shown today" decision holds across every
    // WingCommander host app and standalone FusionWeb — client storage is partitioned per host
    // and can't. welcome_gate_resolved caches the resolution; welcome_eligible_today is the
    // cached decision. Both reset on user switch (see the user-switch reset below).
    welcome_gate_resolved: false,
    welcome_eligible_today: false,
    // Whether this is the user's FIRST open of Rey today. Resolved from the same day marker the
    // welcome gate reads, at the same moment, and then left alone.
    //
    // Deliberately NOT welcome_eligible_today, which looks like the same fact and is not: that one is
    // CONSUMED. FusionBody marks the welcome shown the instant it decides to show it, which flips
    // welcome_eligible_today to false — and MessengerWindow's landing decision runs after that, so by
    // the time it asks, "is the splash still owed?" always answers no. This one records what the day
    // marker said and survives MarkWelcomeShown, so the landing rules can still tell a first open of
    // the day from a later one. Resets on user switch with the gate.
    //
    // Survives a re-mount of the Spark panel too, which is why it is not enough on its own: see
    // rey_startup_landing_done.
    rey_first_open_today: false,
    // True once this page has placed the user in Rey for the first time, whichever way it went — the
    // first-open landing, a question typed on the welcome screen, or a thread they were sent to. The
    // first-open landing (the day's brief, else a New Topic) is taken only while this is false.
    //
    // rey_first_open_today cannot answer "has that already happened": it describes the day, not this
    // page, and it stays true until tomorrow. The Spark panel re-mounts within one page — an auth
    // round-trip unmounts every body, and the welcome splash replaces the chat window while it is up
    // — and each new mount runs the landing again. Without this, the second mount on a first-open day
    // re-took the first-open branch and pulled the user off the conversation they had just gone to.
    // Resets on user switch with the rest of the landing state.
    rey_startup_landing_done: false,
    // Thread id of the last Rey conversation this user had open (Ux.ReyLastThreadId), so reopening
    // later the same day lands back on it. null = none remembered. The zero GUID means they were on
    // a New Topic draft — the same value MessengerWindow uses as a draft's selected_thread — and
    // reopens New Topic rather than being looked up as a thread. rey_last_thread_resolved caches
    // the read the same way welcome_gate_resolved does, so re-mounts cost nothing.
    rey_last_thread_id: null,
    rey_last_thread_resolved: false,
    user_access_info: null,
    // Dealer groups available to this user (enterprise groups + own user groups), fetched from
    // SparkApi GET /api/dealergroups for the dealership-filter picker. null = not loaded yet.
    dealer_groups: null,
    url_cif: null,
    // Whether FusionWeb owns which Rey conversation is open, so the multi-conversation controls may
    // show and the thread the host pinned counts as its default rather than a deep link. Set once by
    // FusionMain from the entry: always true on the toolbar entry (standalone, ERA and any app that
    // embeds the toolbar), where FusionWeb resolves the Rey thread itself; true on the Spark entry only
    // when the host asks for it with ?rey_conversations=1. The WingCommander extension does that from
    // the version that stopped reloading the Rey blade whenever its own cached thread id changed for
    // the same user — older versions did, which reverted every conversation switch made in the blade,
    // and they must keep getting the single-thread panel. Pure host config: not reset on user switch.
    host_supports_rey_conversations: false,
    // Deployment opt-in via FUSION_REY_CONVERSATIONS_IN_HOST (window.__fusionReyConversationsInHost):
    // shows the conversation controls in embedded hosts — visibility ONLY, does not change thread
    // landing (?thread_id= stays a deep link) or last-thread memory, unlike
    // host_supports_rey_conversations above.
    host_shows_conversation_controls: false,
    // Deployment opt-in via FUSION_OPTIONS_MENU_IN_HOST (window.__fusionOptionsMenuInHost):
    // shows the ⋯ options button in the Spark navbar, so hosts that embed the Spark entry and
    // never send SHOW-OPTIONS can still reach the OptionsMenu. Set once by FusionMain.
    host_shows_options_menu: false,
    // The caller's own ephemeral documents in the open thread, keyed for one fetch per thread: every
    // attachment card offers to promote its file, and each resolving its own id would issue one request
    // per card for the same list. thread_documents_for records which thread the list belongs to, so
    // switching threads refetches instead of showing the previous conversation's files.
    thread_documents: [],
    thread_documents_for: null,
    // Whether the open store may attach files at all, and for which CIF the answer was given. The
    // composer offers the picker whenever the mode is Rey's — it cannot read the per-store add-on flag —
    // so without this a user at a store without attachments staged a file, typed a question, and only
    // learned at send. null means "not asked yet", which every reader must treat as ALLOWED: see
    // LoadAttachmentAvailability.
    attachments_availability: null,
    attachments_availability_for: null,
    // Bumped whenever the held answer stops being about the current caller. Same device, same store, a
    // different person is a different answer — and attachments_availability_for keys on the CIF alone, so
    // it cannot see that. A reply is written only if this still reads what it did when the request went
    // out, which is what stops a request in flight during a user switch from restoring the outgoing
    // user's verdict. Sibling of scheduled_availability_generation; the same defect, the same shape.
    attachments_availability_generation: 0,
    unreads_by_thread_id: {},
    channel_threads: [],
    user_threads: [],
    object_threads: [],
    channel_threads_processing: false,
    user_threads_processing: false,
    object_threads_processing: false,
    enterprise_users_data: null,
    enterprise_users_processing: false,
    scheduled_tasks: [],
    scheduled_tasks_processing: false,
    // Whether the scheduled-tasks beta is available at the caller's rooftop: { enabled, state } once
    // known, null until then. null means "not established" and must never render as a refusal — the
    // panel used to have no way to ask at all (the only read was admin-gated), so it offered create
    // buttons on stores that refuse every write.
    scheduled_availability: null,
    // Bumped every time the held answer stops being about the current caller or rooftop. A reply is
    // written only if this still reads what it did when the request went out, which is what stops a
    // request already in flight from landing afterwards and restoring the answer that was just cleared.
    // The counter rather than a comparison of ids: availability depends on BOTH who is asking and which
    // store, and a same-device user switch usually leaves the rooftop untouched, so no single id can
    // stand in for "this answer is stale". Lives here, not in a component, so the clear and the bump on
    // a user switch happen together with nothing observable in between.
    scheduled_availability_generation: 0,
    internal_messaging_state: null,
    messenger_search_active: false,
    messenger_search_query: '',
    open_contact_guid: null,
    pending_new_message_recipient_guid: null,
    // Breadcrumb: when the user leaves an OpenContact profile toward the New Message screen or a
    // shared chat, this holds that contact's personGuid so the next Back press returns to the
    // profile instead of the messenger list. Consumed (cleared) on that Back press; also cleared
    // by ResetMessengerSession on tab/user switches so it can't go stale.
    contact_return_guid: null,
    // Breadcrumb: records which view an OpenContact profile was opened from so the next Back press
    // returns there instead of always landing on the Contacts list. null/default → the Contacts
    // list. 'ChatDetails' → the Chat/Thread Details page (set when a participant row is tapped from
    // a thread/channel's participant list). Consumed (cleared) on that Back press; also cleared by
    // ResetMessengerSession on tab/user switches so it can't go stale.
    contact_origin_view: null,
    // Breadcrumb: the threadId whose Chat/Thread Details page a "shared chat" detour was launched
    // from. Opening a shared chat overwrites the live thread context (nav meta, roster, selected
    // thread), so this remembers the conversation to re-open when the user backs out of the contact
    // profile into the details page. Set by MessengerWindow when a shared-chat open happens during a
    // ChatDetails-originated contact detour; consumed (cleared) on that Back press. Also cleared by
    // ResetMessengerSession / ClearUserScopedState so it can't go stale.
    chat_details_thread_id: null,
    // Snapshot of the details page's conversation context ({ navMeta, participants, participantGuids })
    // captured the instant a shared-chat detour begins — before opening the shared chat overwrites
    // the live thread context. Backing out of the contact profile restores the Chat/Thread Details
    // page from this snapshot with no network reload; the actual thread re-open is deferred until the
    // user backs out of the details page into the conversation itself. Paired with chat_details_thread_id.
    chat_details_nav_snapshot: null,
    // Store mirror of the Internal Messaging MessengerWindow's component-local selected_thread (the
    // conversation currently open in the messages surface). Kept in sync by a watcher there. Lets other
    // subviews — e.g. the contact profile's Shared Chats list — recognize and disable the row for the
    // chat you're already in. Cleared by ResetMessengerSession on tab/user switches.
    //
    // Internal Messaging only. The toolbar mounts MessengerWindow twice, and while the Spark instance
    // wrote here too the two overwrote each other — so the Rey list's current-row highlight followed
    // whatever internal chat was open. The Spark instance mirrors into spark_active_thread_id instead.
    active_thread_id: null,
    new_thread_create_ready: false,
    is_creating_thread: false,
    add_participants_count: 0,
    header_override: null,
    active_thread_participant_guids: [],
    active_thread_nav_meta: null,

    // [NPM-71] Busy state for the three navbar thread actions. These live in the store
    // rather than in MessengerWindow because the buttons are in Navbar while the requests
    // are in MessengerWindow. A return channel over the event bus was rejected:
    // MessengerWindow does a $off in beforeUnmount, so unmounting mid-request would strand
    // the button disabled forever. The store has no such failure mode.
    // MessengerWindow owns the transitions (see its computeds) so the flag spans the whole
    // user-visible operation, not just the HTTP call.
    is_resetting_ai_thread: false,
    is_deleting_thread: false,
    is_leaving_thread: false,

    // ── Rey multi-conversation ────────────────────────────────────────────────────────────
    // Which Spark surface the body is showing: the chat itself, or the conversation list the
    // hamburger swaps in for it. Store-owned because the control is in Navbar and the surface is
    // rendered by Messaging.vue — same split, and same reasoning, as the busy flags above.
    spark_view: 'Chat',
    // The caller's Rey conversations, most recently used first, as returned by the list endpoint.
    spark_conversations: [],
    is_loading_spark_conversations: false,
    // True while the user is in a conversation that exists only on the client. The "+" control
    // creates one of these; nothing is written to Cosmos until they actually ask something, so
    // opening a conversation and changing your mind leaves no empty row in the list.
    spark_draft_active: false,
    // Store mirror of the SPARK MessengerWindow's selected_thread — the Rey conversation open behind
    // the list — for the list's current-row highlight and its "was that the open one?" check on
    // delete. Written only by the Spark instance; active_thread_id above is the Internal Messaging
    // mirror. Deliberately not cleared by ResetMessengerSession: a toolbar tab switch leaves the Spark
    // panel mounted on the same conversation, and nothing would put the value back.
    spark_active_thread_id: null,
    // A Rey conversation some other surface has asked the panel to open — a scheduled result opened
    // from the results inbox, or one the WingCommander extension forwarded from another blade — set
    // by RequestSparkConversationOpen. SPARK-OPEN-CONVERSATION reaches a Spark panel that is already
    // mounted; this is for the one that is not yet: the welcome splash replaces the chat window while
    // it is up, and a fresh blade may still be starting. The panel consumes it on its first
    // getThreads, or clears it when the bus event lands first.
    spark_pending_open_thread_id: null,
    // Resolved member list ({ id, name, email, avatar }) for the open channel, populated from the
    // thread-join response (which the API builds from the thread's ParticipantIds). Drives the
    // navbar's "view participants" panel. Reset on every thread switch.
    active_thread_participants: [],
    messenger_api_base_uri: import.meta.env.VITE_MESSENGER_API_BASE_URI,
    messenger_base_uri: import.meta.env.VITE_MESSENGER_BASE_URI,
    analytics_api_base_uri: ((import.meta.env.VITE_ANALYTICS_API_BASE_URI || import.meta.env.VITE_MESSENGER_BASE_URI) || '').replace(/\/$/, ''),
    theme: 'light', // 'light' | 'dark' | 'system'
    // Active UI language — 'en' | 'es' | 'fr' | 'de' (primary subtag, not the full Ux.Language tag).
    // Lets components read the language without importing the i18n instance; always change it
    // through setLanguage(). Existing consumers (GetTacTeams' lang param, the MessengerWindow
    // '=== en' branches, getStatusMessage) rely on the primary subtag — keep it that way. The
    // regional catalog ('en-GB', 'fr-FR') is i18n.global.locale; the full user tag is languageTag.
    language: DEFAULT_LOCALE,
    // Full BCP-47 tag the user selected ('en-US', 'fr-FR', 'en-GB'), for Intl formatting and the
    // document lang attribute. Populated by setLanguage/initLanguage alongside language.
    languageTag: null,
    wingman_config: null,
    is_read_only: false,
    context_screen_name: null,
    context_control_id: null,
    fusion_release_version: null,
    status_messages: null,
    ai_thread_obj: null,
    ai_thread_loading: false,
    // Rey session tracking (WS-3)
    // NOTE: rey_session_id is a client-generated analytics UUID scoped to the toolbar lifetime.
    // It is NOT the dlrSecured/Fusion auth session — auth identity lives in the Bearer token.
    rey_session_id: null,
    rey_session_started_at: null,
    rey_action_count: 0,
    rey_session_timeout_handle: null,
    // True when this iframe created the IndexedDB session_state record; false when it adopted a
    // sibling iframe's existing session. Only owners emit rey_session_ended on endReySession.
    rey_is_session_owner: false,
    // WS-5: active AI chat conversation tracking (store-level so endReySession can close it)
    ai_chat_thread_id: null,
    ai_chat_started_at: null,
    ai_chat_message_count: 0,
    debug_logs: [],
  }),

  getters: {
    getStatusMessage: (state) => (key, lang) => {
      if (!state.status_messages) return '';
      const keyMap = state.status_messages[key];
      if (!keyMap) return '';
      const langCode = (typeof lang === 'string' && lang) ? lang.split('-')[0].toLowerCase() : 'en';
      return keyMap[langCode] || keyMap['en'] || '';
    },
    auth_request_headers_dto(state) {
      if (state.token) {
        const headers = { Authorization: `Bearer ${state.token}` };
        if (state.rey_session_id)
          headers['X-Rey-Session-Id'] = state.rey_session_id;
        return { headers };
      }

      return null;
    },
    threads_type_by_thread_id(state) {
      let fieldData = {};
      if (state.channel_threads.length > 0) {
        state.channel_threads.forEach(thread => {
          fieldData[thread.threadId] = 'Channel';
        });
      }
      if (state.user_threads.length > 0) {
        state.user_threads.forEach(thread => {
          fieldData[thread.threadId] = 'User';
        });
      }
      if (state.object_threads.length > 0) {
        state.object_threads.forEach(thread => {
          fieldData[thread.threadId] = thread.objectType;
        });
      }

      return fieldData;
    },
    internal_messages_unread_count(state) {
      if (Object.keys(state.unreads_by_thread_id).length > 0 && Object.keys(this.threads_type_by_thread_id).length > 0) {
        return Object.keys(state.unreads_by_thread_id).reduce((total, threadId) => {
          let unreadCount = state.unreads_by_thread_id[threadId] || 0;
          let threadType = this.threads_type_by_thread_id[threadId] || '';
          if (threadType.startsWith('SystemAI_'))
            return total;
          if (!this.has_tac_access && threadType === 'TACChat')
            return total;
          if (!this.has_chat_access && threadType !== 'TACChat')
            return total;

          return total + (unreadCount || 0);
        }, 0);
      }

      return 0;
    },
    spark_unread_count(state) {
      if (this.has_ask_rey_access && Object.keys(state.unreads_by_thread_id).length > 0 && Object.keys(this.threads_type_by_thread_id).length > 0) {
        return Object.keys(state.unreads_by_thread_id).reduce((total, threadId) => {
          let unreadCount = state.unreads_by_thread_id[threadId] || 0;
          let threadType = this.threads_type_by_thread_id[threadId] || '';
          if (threadType.startsWith('SystemAI_'))
            return total + (unreadCount || 0);

          return total;
        }, 0);
      }

      return 0;
    },
    has_ask_rey_access(state) {
      if (import.meta.env.VITE_OVERRIDE_ACCESS_RIGHTS === 'true') return true;

      if (!state.user_access_info?.featureAccess) return false;
      const feature = state.user_access_info.featureAccess.find(f => f.featureId === 'ask_rey');
      return feature?.allowed ?? false;
    },
    // Fusion entitlement (FrontRunner, dlrSecured REYUSER, or trusted-app). Without it the server
    // 403s the Rey (SystemAI) thread, so the Rey surface shows the not-authorized message.
    // Only an explicit false denies: a SparkApi that predates the field leaves Rey as it was.
    has_fusion_entitlement(state) {
      if (import.meta.env.VITE_OVERRIDE_ACCESS_RIGHTS === 'true') return true;

      return state.user_access_info?.hasFusionEntitlement !== false;
    },
    has_tac_access(state) {
      if (import.meta.env.VITE_OVERRIDE_ACCESS_RIGHTS === 'true') return true;

      if (!state.user_access_info?.featureAccess) return false;
      const feature = state.user_access_info.featureAccess.find(f => f.featureId === 'tac');
      return feature?.allowed ?? false;
    },
    has_mcp_access(state) {
      if (import.meta.env.VITE_OVERRIDE_ACCESS_RIGHTS === 'true') return true;

      if (!state.user_access_info?.featureAccess) return false;
      const feature = state.user_access_info.featureAccess.find(f => f.featureId === 'mcp');
      return feature?.allowed ?? false;
    },
    has_chat_access(state) {
      if (import.meta.env.VITE_OVERRIDE_ACCESS_RIGHTS === 'true') return true;

      if (!state.user_access_info?.featureAccess) return false;
      const feature = state.user_access_info.featureAccess.find(f => f.featureId === 'chat');
      return feature?.allowed ?? false;
    },
    has_consumer_access(state) {
      if (import.meta.env.VITE_OVERRIDE_ACCESS_RIGHTS === 'true') return true;

      if (!state.user_access_info?.featureAccess) return false;
      const feature = state.user_access_info.featureAccess.find(f => f.featureId === 'consumer');
      return feature?.allowed ?? false;
    },
    has_autovision_access(state) {
      if (import.meta.env.VITE_OVERRIDE_ACCESS_RIGHTS === 'true') return true;

      const list = state.user_access_info?.mcpAccess;
      if (!Array.isArray(list)) return false;
      return list.some(m => m && m.hasAccess && typeof m.serverName === 'string'
        && m.serverName.toLowerCase() === 'autovisionmcp');
    },
    all_dealers(state) {
      return state.user_access_info?.dealers || [];
    },
    // Dealer groups the caller can actually use, sorted by name, each with accessibleCount (how many of the
    // caller's own stores it holds): user groups as-is, enterprise groups only when they share at least one
    // store with the caller. The same rule the chat composer's dealership filter applies (MessengerInput's
    // usable_groups); the server re-applies it wherever a group is accepted.
    usable_dealer_groups(state) {
      const mine = new Set((state.user_access_info?.dealers || []).map(d => (d.cifNumber || '').toLowerCase()));
      return (state.dealer_groups || [])
        .map(g => ({
          ...g,
          accessibleCount: (g.memberCifs || []).filter(c => mine.has((c || '').toLowerCase())).length,
        }))
        .filter(g => g.scope === 'User' || g.accessibleCount > 0)
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },
    // Reynolds-employee / enterprise admin flag from the access info. Client-side gate for
    // showing the Scheduled Tasks admin surface; the API re-checks server-side (never trusts this).
    is_fusion_admin(state) {
      return state.user_access_info?.isFusionAdmin === true;
    },
    current_cif(state) {
      const initType = state.wingman_config?.FusionInitType;
      // Case-insensitive on purpose. Hosts do not agree on the casing of this value, which is why
      // the WingCommander extension normalises it in four separate places before it reaches us
      // (and accepts both DealerIdType and dealerIdType as the key). A strict === 'CIF' comparison
      // silently yields no CIF for any host that sends 'cif', costing the user their dealer context.
      const wingmanCif = String(initType?.DealerIdType ?? '').toUpperCase() === 'CIF'
        ? initType?.DealerId
        : null;
      return wingmanCif || state.url_cif || null;
    },
    active_dealer() {
      if (!this.current_cif || !this.all_dealers) return null;
      return this.all_dealers.find(dealer => dealer.cifNumber?.toLowerCase() === this.current_cif?.toLowerCase()) || null;
    },
    // True when Fusion is hosted inside the AutoVision application. Detected from the host's
    // wingmanConfig (FusionInitType.InitSystem), which the Chrome extension injects via
    // UPDATE-CONTEXT. This identifies the host platform Fusion is running in — distinct from
    // mcpAccess, which is a per-user permission that travels across every platform.
    is_autovision_platform(state) {
      return (state.wingman_config?.FusionInitType?.InitSystem || '').toUpperCase() === 'AUTOVISION';
    },
  },

  actions: {
    // ── Rey Scheduled Tasks (Track C) ──────────────────────────────────────
    // Backend: SparkApi ScheduledTasksController, routed under api/scheduled (pending — Track C backend).
    GetScheduledTasks() {
      return axios.get(this.messenger_api_base_uri + 'scheduled', this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - GetScheduledTasks`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - GetScheduledTasks`));
          return Promise.reject(error.message);
        });
    },
    // Forget the held scheduled-tasks verdict and supersede any request still in flight for it. Called
    // whenever the answer stops being about the current caller or rooftop — a user switch (below) or a
    // rooftop switch (watched by the surfaces that gate on it).
    InvalidateScheduledAvailability() {
      this.scheduled_availability = null;
      this.scheduled_availability_generation += 1;
    },
    // dealerId names the store the calling surface is actually showing. The server honours it ONLY when
    // it is one of the caller's own associations and otherwise degrades to their first, so passing
    // nothing is safe — it just costs a multi-store user the right rooftop, which is the defect the
    // parameter was added to fix (see ScheduledTasksController.Availability). Every caller should pass
    // current_cif: two surfaces asking about different stores is how the first wrong-store bug survived.
    GetScheduledAvailability(dealerId) {
      const query = dealerId ? `?dealerId=${encodeURIComponent(dealerId)}` : '';
      return axios.get(this.messenger_api_base_uri + 'scheduled/availability' + query, this.auth_request_headers_dto)
        .then(response => {
          if (response.data && response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - GetScheduledAvailability`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - GetScheduledAvailability`));
          return Promise.reject(error.message);
        });
    },
    SetScheduledTaskEnabled(setEnabledDTO) {
      // setEnabledDTO: { id, enabled }
      return axios.post(this.messenger_api_base_uri + 'scheduled/' + setEnabledDTO.id + '/enabled',
        { enabled: setEnabledDTO.enabled }, this.auth_request_headers_dto)
        .then(response => {
          if (response.data && response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - SetScheduledTaskEnabled`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - SetScheduledTaskEnabled`));
          // A spent one-time task cannot be switched back on, and retrying will not change that — so the
          // server's message is passed through, in the same { code } shape SaveScheduledTask uses for the
          // task cap. Every other failure still rejects with the bare message.
          if (error.response?.status === 409 && error.response?.data?.reason === 'one_time_spent')
            return Promise.reject({ code: 'one_time_spent', status: 409, message: error.response.data.message });
          return Promise.reject(error.message);
        });
    },
    RunScheduledTaskNow(taskId) {
      return axios.post(this.messenger_api_base_uri + 'scheduled/' + taskId + '/run', {}, this.auth_request_headers_dto)
        .then(response => {
          if (response.data && response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - RunScheduledTaskNow`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - RunScheduledTaskNow`));
          return Promise.reject(error.message);
        });
    },
    DeleteScheduledTask(taskId) {
      return axios.delete(this.messenger_api_base_uri + 'scheduled/' + taskId, this.auth_request_headers_dto)
        .then(response => {
          if (response.data && response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - DeleteScheduledTask`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - DeleteScheduledTask`));
          return Promise.reject(error.message);
        });
    },
    // Run history for one task: GET api/scheduled/{id}/runs (most recent first, server-limited).
    GetScheduledTaskRuns(taskId) {
      return axios.get(this.messenger_api_base_uri + 'scheduled/' + taskId + '/runs', this.auth_request_headers_dto)
        .then(response => {
          if (response.data && response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - GetScheduledTaskRuns`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - GetScheduledTaskRuns`));
          return Promise.reject(error.message);
        });
    },
    // Audit trail for one task: GET api/scheduled/{id}/audit (owner-scoped, newest first).
    GetScheduledTaskAudit(taskId) {
      return axios.get(this.messenger_api_base_uri + 'scheduled/' + taskId + '/audit', this.auth_request_headers_dto)
        .then(response => {
          if (response.data && response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - GetScheduledTaskAudit`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - GetScheduledTaskAudit`));
          return Promise.reject(error.message);
        });
    },
    // Results inbox (NPM-19): GET api/scheduled/inbox. Every filter is a query parameter on purpose —
    // filtering a page in the browser returns a short page, which reads as the end of the list.
    // `params` carries dealerId / taskType / status / unreadOnly / sinceDays / limit / cursor.
    GetScheduledInbox(params) {
      return axios.get(this.messenger_api_base_uri + 'scheduled/inbox',
        { ...this.auth_request_headers_dto, params: params || {} })
        .then(response => {
          if (response.data && response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - GetScheduledInbox`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - GetScheduledInbox`));
          return Promise.reject(error.message);
        });
    },
    // POST api/scheduled/compile-watcher — a person's own words in, a compiled condition plus the
    // restatement they confirm before anything is created. Nothing is saved by this call.
    //
    // Resolves rather than rejects on a refusal: the server answers 200 with {ok:false, error, canWatch}
    // because an unresolved subject is a normal conversational outcome, not a failed request, and the
    // form shows it as guidance beside the box the person is still typing in.
    CompileWatcherCondition(payload) {
      return axios.post(this.messenger_api_base_uri + 'scheduled/compile-watcher', payload,
        this.auth_request_headers_dto)
        .then(response => {
          if (response.data && response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - CompileWatcherCondition`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - CompileWatcherCondition`));
          return Promise.reject(error.message);
        });
    },
    // POST api/scheduled/compile-event — the Event-trigger card's counterpart to CompileWatcherCondition.
    // Same contract: nothing is saved, and a refusal arrives as 200 with {ok:false, error, canReactTo}.
    CompileEventTrigger(payload) {
      return axios.post(this.messenger_api_base_uri + 'scheduled/compile-event', payload,
        this.auth_request_headers_dto)
        .then(response => {
          if (response.data && response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - CompileEventTrigger`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - CompileEventTrigger`));
          return Promise.reject(error.message);
        });
    },
    // GET api/scheduled/event-types — what Rey can react to. Read rather than hardcoded because the
    // catalog is replaceable at runtime (EventTypeCatalogFile), so a baked-in client list would drift.
    GetScheduledEventTypes() {
      return axios.get(this.messenger_api_base_uri + 'scheduled/event-types', this.auth_request_headers_dto)
        .then(response => {
          if (response.data && response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - GetScheduledEventTypes`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - GetScheduledEventTypes`));
          return Promise.reject(error.message);
        });
    },
    // Mark one inbox row read: POST api/scheduled/{taskId}/runs/{runId}/read (204). The task id is
    // required, not decorative — run history is partitioned by task, so a run id alone is not addressable.
    MarkScheduledRunRead(taskId, runId) {
      return axios.post(
        this.messenger_api_base_uri + 'scheduled/' + taskId + '/runs/' + runId + '/read',
        {}, this.auth_request_headers_dto)
        .then(response => {
          if (response.data && response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - MarkScheduledRunRead`));
            return Promise.reject(errorMsg);
          }
          return true;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - MarkScheduledRunRead`));
          return Promise.reject(error.message);
        });
    },
    // Mark a page read: POST api/scheduled/inbox/mark-read, body [{taskId, runId}]. The server caps the
    // batch at 50 and answers 400 above it rather than truncating, so never send more than one page.
    MarkScheduledInboxRead(rows) {
      return axios.post(this.messenger_api_base_uri + 'scheduled/inbox/mark-read',
        rows || [], this.auth_request_headers_dto)
        .then(response => {
          if (response.data && response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - MarkScheduledInboxRead`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - MarkScheduledInboxRead`));
          return Promise.reject(error.message);
        });
    },
    // Admin: all scheduled tasks in a store. GET api/scheduled/admin/dealer/{dealerId} (IsFusionAdmin, enforced server-side).
    GetScheduledTasksForDealer(dealerId) {
      return axios.get(this.messenger_api_base_uri + 'scheduled/admin/dealer/' + dealerId, this.auth_request_headers_dto)
        .then(response => {
          if (response.data && response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - GetScheduledTasksForDealer`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - GetScheduledTasksForDealer`));
          return Promise.reject(error.message);
        });
    },
    // Admin: store-wide enable/disable. POST api/scheduled/admin/dealer/{dealerId}/enabled.
    SetDealerScheduledTasksEnabled(setDealerEnabledDTO) {
      // setDealerEnabledDTO: { dealerId, enabled }
      return axios.post(this.messenger_api_base_uri + 'scheduled/admin/dealer/' + setDealerEnabledDTO.dealerId + '/enabled',
        { enabled: setDealerEnabledDTO.enabled }, this.auth_request_headers_dto)
        .then(response => {
          if (response.data && response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - SetDealerScheduledTasksEnabled`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - SetDealerScheduledTasksEnabled`));
          return Promise.reject(error.message);
        });
    },
    // What a watcher's condition may be corrected to: the fields its tool can compare and the comparators
    // its template allows. GET api/scheduled/{id}/condition-fields. Caller-scoped, not admin.
    GetWatcherConditionFields(taskId) {
      return axios.get(this.messenger_api_base_uri + 'scheduled/' + taskId + '/condition-fields', this.auth_request_headers_dto)
        .then(response => {
          if (response.data && response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - GetWatcherConditionFields`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - GetWatcherConditionFields`));
          return Promise.reject(error.message);
        });
    },
    // Admin: learned morning-brief facts this store could include, and which are approved.
    // GET api/scheduled/admin/dealer/{dealerId}/brief-facts.
    GetBriefFactApprovals(dealerId) {
      return axios.get(this.messenger_api_base_uri + 'scheduled/admin/dealer/' + dealerId + '/brief-facts', this.auth_request_headers_dto)
        .then(response => {
          if (response.data && response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - GetBriefFactApprovals`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - GetBriefFactApprovals`));
          return Promise.reject(error.message);
        });
    },
    // Admin: replace this store's approved learned brief facts.
    // POST api/scheduled/admin/dealer/{dealerId}/brief-facts.
    //
    // factKeys is the FULL approved set, not a delta: the server replaces. That is why the caller must send
    // back every row the GET returned as approved, including ones nothing mined recently — dropping those
    // would un-approve facts nobody chose to un-approve.
    SetBriefFactApprovals(briefFactApprovalsDTO) {
      // briefFactApprovalsDTO: { dealerId, factKeys }
      return axios.post(this.messenger_api_base_uri + 'scheduled/admin/dealer/' + briefFactApprovalsDTO.dealerId + '/brief-facts',
        { factKeys: briefFactApprovalsDTO.factKeys }, this.auth_request_headers_dto)
        .then(response => {
          if (response.data && response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - SetBriefFactApprovals`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - SetBriefFactApprovals`));
          return Promise.reject(error.message);
        });
    },
    // Create (no id) or update (id present) a scheduled task via the structured editor.
    // POST api/scheduled for create, PUT api/scheduled/{id} for update — matches the controller.
    SaveScheduledTask(task) {
      const isUpdate = task && task.id;
      const url = this.messenger_api_base_uri + 'scheduled' + (isUpdate ? '/' + task.id : '');
      const request = isUpdate
        ? axios.put(url, task, this.auth_request_headers_dto)
        : axios.post(url, task, this.auth_request_headers_dto);
      return request
        .then(response => {
          if (response.data && response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - SaveScheduledTask`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - SaveScheduledTask`));
          // The task cap is the one refusal worth showing verbatim — retrying cannot fix it, and the
          // server's message says what will. Same { code } shape as GetThreadMessages' thread_missing;
          // every other failure still rejects with the bare message, so existing handlers are unaffected.
          if (error.response?.status === 409 && error.response?.data?.reason === 'task_cap_reached')
            return Promise.reject({ code: 'task_cap_reached', status: 409, message: error.response.data.message });
          return Promise.reject(error.message);
        });
    },

    // ── Act-with-approval (§5.7): the owner's draft approval queue ─────────────────────────────
    // GET api/scheduled/actions — pending drafts (bare array).
    // GET api/scheduled/actions?status=settled — what already happened. Its own action rather than a
    // parameter on GetActionDrafts, because the queue and the history are two lists on screen at once and
    // sharing one action would have them overwrite each other's state.
    GetSettledActionDrafts() {
      return axios.get(this.messenger_api_base_uri + 'scheduled/actions', { ...this.auth_request_headers_dto, params: { status: 'settled' } })
        .then(response => response.data)
        .catch(error => { console.error(new Error(`${error.message} - GetSettledActionDrafts`)); return Promise.reject(error.message); });
    },
    GetActionDrafts() {
      return axios.get(this.messenger_api_base_uri + 'scheduled/actions', this.auth_request_headers_dto)
        .then(response => response.data)
        .catch(error => { console.error(new Error(`${error.message} - GetActionDrafts`)); return Promise.reject(error.message); });
    },
    // POST api/scheduled/actions/{id}/approve | /dismiss.
    SetActionDraftDecision({ id, decision }) {
      return axios.post(this.messenger_api_base_uri + 'scheduled/actions/' + id + '/' + decision, {}, this.auth_request_headers_dto)
        .then(response => response.data)
        .catch(error => { console.error(new Error(`${error.message} - SetActionDraftDecision`)); return Promise.reject(error.message); });
    },
    // PUT api/scheduled/actions/{id} — edit a pending draft (versioned server-side).
    EditActionDraft({ id, content }) {
      return axios.put(this.messenger_api_base_uri + 'scheduled/actions/' + id, { content }, this.auth_request_headers_dto)
        .then(response => response.data)
        .catch(error => { console.error(new Error(`${error.message} - EditActionDraft`)); return Promise.reject(error.message); });
    },
    // POST api/scheduled/actions/batch-approve — one audited decision over many drafts.
    BatchApproveActionDrafts(ids) {
      return axios.post(this.messenger_api_base_uri + 'scheduled/actions/batch-approve', { ids }, this.auth_request_headers_dto)
        .then(response => response.data)
        .catch(error => { console.error(new Error(`${error.message} - BatchApproveActionDrafts`)); return Promise.reject(error.message); });
    },

    // ── AI schedule suggestions (§5.2): opt-in offer to automate a repeated question ────────────
    // GET api/scheduled/suggestions — current suggestion (204 → null).
    GetScheduleSuggestion(dealerId, timeZone) {
      const params = [];
      if (dealerId) params.push(`dealerId=${encodeURIComponent(dealerId)}`);
      if (timeZone) params.push(`timeZone=${encodeURIComponent(timeZone)}`);
      const query = params.length ? `?${params.join('&')}` : '';
      return axios.get(this.messenger_api_base_uri + 'scheduled/suggestions' + query, this.auth_request_headers_dto)
        .then(response => (response.status === 204 ? null : response.data))
        .catch(error => { console.error(new Error(`${error.message} - GetScheduleSuggestion`)); return Promise.reject(error.message); });
    },
    // POST api/scheduled/suggestions/accept — create the offered schedule.
    // Rejects with what the SERVER said, not with "Request failed with status code 409": this call is
    // refused for two reasons a user can act on — the rooftop is not in the beta, or they are at their
    // task cap — and the axios message names neither.
    AcceptScheduleSuggestion(payload) {
      return axios.post(this.messenger_api_base_uri + 'scheduled/suggestions/accept', payload, this.auth_request_headers_dto)
        .then(response => response.data)
        .catch(error => {
          const reason = error.response?.data?.message || error.response?.data?.error || error.message;
          console.error(new Error(`${reason} - AcceptScheduleSuggestion`));
          return Promise.reject(reason);
        });
    },
    // ── §5.5 feedback on a delivered scheduled result / §5.3 engagement ─────────────────────────
    // POST api/scheduled/{id}/feedback — shapes how the NEXT output reads. Never what it checks.
    // Resolves to null on failure rather than rejecting. Every call site is a secondary write beside a
    // message-feedback call the UI actually reflects, so a rejection here would only be an obligation to
    // remember to catch — and forgetting once turns a task-feedback outage into an unhandled rejection in
    // a chat window.
    SubmitScheduledTaskFeedback(taskId, kind, note) {
      return axios.post(`${this.messenger_api_base_uri}scheduled/${encodeURIComponent(taskId)}/feedback`,
        { kind, note }, this.auth_request_headers_dto)
        .then(response => response.data)
        .catch(error => {
          const reason = error.response?.data?.message || error.message;
          console.error(new Error(`${reason} - SubmitScheduledTaskFeedback`));
          return null;
        });
    },
    // POST api/scheduled/{id}/engagement — the owner looked at a result. Best-effort by design: this
    // resets an auto-pause clock, and failing to record a visit must never disturb reading the thread.
    RecordScheduledTaskEngagement(taskId) {
      return axios.post(`${this.messenger_api_base_uri}scheduled/${encodeURIComponent(taskId)}/engagement`,
        {}, this.auth_request_headers_dto)
        .then(response => response.data)
        .catch(error => { console.error(new Error(`${error.message} - RecordScheduledTaskEngagement`)); return null; });
    },
    // POST api/scheduled/suggestions/dismiss — remember the dismissal (cooldown).
    DismissScheduleSuggestion(normalizedQuestion) {
      return axios.post(this.messenger_api_base_uri + 'scheduled/suggestions/dismiss', { normalizedQuestion }, this.auth_request_headers_dto)
        .then(response => response.data)
        .catch(error => { console.error(new Error(`${error.message} - DismissScheduleSuggestion`)); return Promise.reject(error.message); });
    },

    async GetWebSocketUrl() {
      return axios.get(this.messenger_api_base_uri + 'GetWebSocketURL', this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - GetWebSocketURL`));
            return Promise.reject(errorMsg);
          }
          return response.data?.url || null;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - GetWebSocketURL`));
          return Promise.reject(error.message);
        });
    },
    async ConnectSocket() {
      if (_socket?.readyState === WebSocket.OPEN || _socket?.readyState === WebSocket.CONNECTING) {
        this.socket_connected = (_socket.readyState === WebSocket.OPEN);
        this.socket_connecting = (_socket.readyState === WebSocket.CONNECTING);
        return;
      }
      if (this.socket_connecting) return;

      this.socket_connecting = true;
      this.socket_try_again = false;

      try {
        const url = await this.GetWebSocketUrl();
        if (!url) {
          this.socket_connecting = false;
          this.socket_try_again = true;
          return;
        }
        _socket = new WebSocket(url);
        _socket.onopen  = () => this._onSocketOpen();
        _socket.onmessage = (event) => this._onSocketMessage(event);
        _socket.onclose = () => this._onSocketClose();
        _socket.onerror = (error) => this._onSocketError(error);
      } catch (e) {
        this.socket_connecting = false;
        this.socket_try_again = true;
      }

      if (!_visibilityHandlerAttached) {
        window.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible' && !this.socket_connected && !this.socket_connecting)
            this.TryAgainSocket();
        });
        _visibilityHandlerAttached = true;
      }
    },
    _onSocketOpen() {
      console.log('Connected to Azure Web PubSub via WebSocket');
      this.socket_connected = true;
      this.socket_connecting = false;
      this.socket_retries = 0;
      // A hard navigation reloads the bundle and resets _hasConnectedBefore, so also consult the
      // sessionStorage marker, which survives a same-tab reload. If EITHER says we've connected before,
      // treat this open as a RE-connect so the reconcile fires. Reading storage is best-effort — private
      // mode or a sandboxed iframe can throw — in which case we fall back to the in-memory flag alone.
      let connectedBeforeReload = false;
      try { connectedBeforeReload = window.sessionStorage.getItem(WS_CONNECTED_BEFORE_KEY) === '1'; } catch (e) { /* sessionStorage unavailable */ }
      const wasReconnect = _hasConnectedBefore || connectedBeforeReload;   // first open of the session must NOT trigger a refetch
      _hasConnectedBefore = true;
      try { window.sessionStorage.setItem(WS_CONNECTED_BEFORE_KEY, '1'); } catch (e) { /* best-effort */ }
      this.JoinUserToThreads().then(() => {
        console.log('Successfully joined user to threads');
      }).catch(error => {
        console.error('Error joining user to threads:', error);
      }).finally(() => {
        // On a RE-connect the panel may have missed the live stream of an in-flight or just-finished Rey
        // turn (e.g. a hard navigation reloaded it). Signal open surfaces to reconcile from durable
        // history — the answer is persisted in Cosmos even when its live WebSocket delivery was lost.
        // Emitted from finally, not then(): the reconcile is an independent history refetch, not a
        // socket-group operation, so a FAILED JoinUserToThreads must not strand a reconnected client with
        // a missing answer. First connects still never emit (wasReconnect gates it).
        if (wasReconnect)
          window.event_bus.$emit('WS-RECONNECTED');
      });
    },
    _onSocketMessage(event) {
      const data = convertKeysToCamelCase(JSON.parse(event.data));
      console.log('Message received through WebSocket:', data);
      window.event_bus.$emit('WS-MESSAGE', data);
    },
    _onSocketClose() {
      console.log(`WebSocket connection closed (${document.visibilityState})`);
      this.CleanupSocket();
      if (this.socket_retries > 0) {
        this.socket_try_again = true;
        window.event_bus.$emit('WS-CONNECTION-ERROR', i18n.global.t('messaging.window.errors.connectionClosed'));
      } else {
        this.socket_retries += 1;
        this.TryAgainSocket();
      }
    },
    _onSocketError(error) {
      console.error('WebSocket error:', error);
      this.CleanupSocket();
      if (this.socket_retries > 0) {
        this.socket_try_again = true;
        window.event_bus.$emit('WS-CONNECTION-ERROR', i18n.global.t('messaging.window.errors.connectionError'));
      } else {
        this.socket_retries += 1;
        this.TryAgainSocket();
      }
    },
    CleanupSocket() {
      if (_socket) {
        _socket.onopen = null;
        _socket.onmessage = null;
        _socket.onclose = null;
        _socket.onerror = null;
        if (_socket.readyState === WebSocket.OPEN || _socket.readyState === WebSocket.CONNECTING)
          _socket.close();
        _socket = null;
        console.log('Cleaning up WebSocket connection');
      } else {
        console.log('WebSocket already closed');
      }
      this.socket_connected = false;
      this.socket_connecting = false;
    },
    async TryAgainSocket() {
      this.socket_try_again = false;
      await this.ConnectSocket();
    },
    async JoinUserToThreads() {
      return axios.get(this.messenger_api_base_uri + 'JoinUserToThreads', this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - JoinUserToThreads`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - ${JoinUserToThreadsDTO}`));
          return Promise.reject(error.message);
        });
    },
    ResetMessengerSession() {
      // Clears transient state owned by the InternalMessaging page so it doesn't leak across
      // FusionToolbar tab switches. Called by FusionToolbar.setActiveTab on both enter and
      // leave so background thread auto-opens (e.g. from a thread_id URL param) don't bleed
      // into the navbar when the user first visits the tab.
      this.messenger_search_active = false;
      this.messenger_search_query = '';
      this.open_contact_guid = null;
      this.pending_new_message_recipient_guid = null;
      this.contact_return_guid = null;
      this.contact_origin_view = null;
      this.chat_details_thread_id = null;
      this.chat_details_nav_snapshot = null;
      this.active_thread_id = null;
      // Open-thread navbar metadata group (siblings of active_thread_id): the avatar/name meta and
      // resolved roster for whatever conversation was open. Cleared here too so a stale header/avatar
      // can't bleed into the navbar after a tab/user switch.
      this.active_thread_nav_meta = null;
      this.active_thread_participant_guids = [];
      this.active_thread_participants = [];
      // New-thread-creation transients owned by the compose/CreateThread flow. CreateThread clears
      // these in beforeUnmount, but that never fires on a tab switch (FusionBody stays mounted via
      // v-show), so we must clear at this seam: is_creating_thread otherwise stays stuck (blocking
      // the Create button and re-compose) and add_participants_count feeds a stale "Add (N)" label.
      this.is_creating_thread = false;
      this.new_thread_create_ready = false;
      this.add_participants_count = 0;
      this.header_override = null;
      this.internal_messaging_state = null;
    },
    // [Ignite credential desync] Resets the SERVER session when the host's per-user identity
    // changed without the host JWT changing (ERA-IGNITE users at one dealership share a
    // DlrSecured account: sub/email identical across an Ignite logout → login as a different
    // ERA user, so Authorize's IsIdentitySwitch correctly reports "same user" and would answer
    // same_user forever). The MVC /logout route is the one thing that clears the ASP.NET
    // session (user_token / user_id / uu_id); after it, the next Authorize runs
    // PerformTrustedLogin and mints fresh for whoever the token says. Fire-and-forget from the
    // caller's perspective: if the POST fails (offline, host down) the re-authenticate that
    // follows still succeeds via the normal path — worst case the old session persists, which
    // is exactly the shipped behaviour.
    async EndHostUserSession() {
      // Plain axios.post — relative URL hits FusionWeb itself (the MVC /logout route), NOT
      // messenger_api_base_uri (that's SparkApi, which has no logout). No auth headers: the
      // whole point is to clear the session, and the route requires none.
      try {
        await axios.post('/logout', {});
      } catch (error) {
        console.warn('EndHostUserSession: /logout POST failed — continuing with re-authenticate', error?.message ?? error);
      }
      this.ClearUserScopedState();
    },
    ClearUserScopedState() {
      // Wipes all per-user chat data so it cannot leak across a same-device user switch
      // (logout → a different user logs in within the same SPA instance, where the Pinia
      // store is never recreated). Called from FusionMain.authenticateViaAjax when the auth
      // layer reports action === 'user_switched'. token / user_access_info are intentionally
      // left alone — the auth flow overwrites them for the incoming user immediately after
      // (getUserAccessInfo is awaited right after this call). Pure host config (url_cif, base
      // URIs, theme, wingman_config) is not user data and is left as-is.

      // Cleanly end the outgoing user's Rey analytics session (also closes any active AI chat,
      // resetting ai_chat_thread_id / ai_chat_started_at / ai_chat_message_count and emitting
      // rey_session_ended for the previous user).
      this.endReySession('user_closed');

      // Thread lists and their loading flags.
      this.channel_threads = [];
      this.user_threads = [];
      this.object_threads = [];
      this.channel_threads_processing = false;
      this.user_threads_processing = false;
      this.object_threads_processing = false;

      // Unread counts and AI/conversation context.
      this.unreads_by_thread_id = {};
      this.ai_thread_obj = null;
      this.ai_thread_loading = false;
      this.thread_context = null;
      this.internal_messaging_state = null;
      this.enterprise_users_data = null;
      this.enterprise_users_processing = false;

      // Per-user settings — lazily loaded by the settings UI and never re-fetched on switch,
      // so they must be cleared here (defaults to null; all consumers already handle null).
      this.user_settings = null;

      // Whether the scheduled-tasks beta is open is answered per caller as well as per rooftop, and like
      // the settings above it is lazily loaded and never re-fetched on switch — so without this the
      // incoming user inherits the outgoing user's verdict, and the surfaces that gate on it (the options
      // menu entry, the panel's create button) are decided by someone else's access. Clearing alone is
      // not enough: the outgoing user's request may still be in flight, and the rooftop it was asked
      // about is usually unchanged, so only the generation bump can tell that reply it is stale.
      this.InvalidateScheduledAvailability();

      // Attachments are answered per caller too, and the composer now hides the picker unless the answer
      // says yes — so an inherited verdict decides whether the incoming user is offered uploading at all.
      this.InvalidateAttachmentAvailability();

      // The ReyWelcome gate is derived from the outgoing user's settings; clear it so the
      // incoming user re-resolves their own "shown today" state instead of inheriting it.
      this.welcome_gate_resolved = false;
      this.welcome_eligible_today = false;
      // Both halves of the Rey landing decision come from those same settings, so they inherit the
      // same way. The remembered thread is the one that matters: left in place, the incoming user's
      // first open would try to land on a conversation belonging to the outgoing one — which the
      // backend now correctly rejects with a 403.
      this.rey_first_open_today = false;
      this.rey_last_thread_id = null;
      this.rey_last_thread_resolved = false;
      this.rey_startup_landing_done = false;

      // The Rey conversation list and the panel state around it. The list renders straight out of
      // spark_conversations the moment it mounts and only then refetches, so without this the
      // incoming user saw the outgoing user's Topics rows until the read came back; and an inherited
      // spark_draft_active made their first send create a conversation instead of using the one on
      // screen. Hosts that switch users in place — the WingCommander extension and ERA both do —
      // are exactly where the list now appears.
      this.spark_view = 'Chat';
      this.spark_conversations = [];
      this.is_loading_spark_conversations = false;
      this.spark_draft_active = false;
      this.spark_active_thread_id = null;
      this.spark_pending_open_thread_id = null;

      // The Schedules panel's task list, for the same reason: it renders the held list while its own
      // refetch is in flight.
      this.scheduled_tasks = [];
      this.scheduled_tasks_processing = false;

      // Messaging UI transients (search/open contact/pending recipient).
      this.ResetMessengerSession();
    },
    GetThreads(GetThreadsDTO) {
      return axios.get(this.messenger_api_base_uri + 'threads/' + GetThreadsDTO, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - ${GetThreadsDTO}`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - ${GetThreadsDTO}`));
          return Promise.reject(error.message);
        });
    },
    GetAIThread(GetAIThreadDTO) {
      return axios.get(this.messenger_api_base_uri + 'threads/system/' + GetAIThreadDTO + '/ai', this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - ${GetAIThreadDTO}`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - ${GetAIThreadDTO}`));
          return Promise.reject(error.message);
        });
    },
    // Asks the Spark panel to open one Rey conversation, from a surface that does not own it — the
    // Schedules results inbox, or the WingCommander extension forwarding an open from another blade.
    // The pending id covers a panel that is not mounted yet (see spark_pending_open_thread_id); the
    // bus event covers one that is, and also takes the welcome splash down (FusionBody), since the
    // user has just said where they want to be. The list view is left for the chat for the same
    // reason ReyConversationList.openConversation leaves it.
    RequestSparkConversationOpen(threadId) {
      if (!threadId) return;
      this.spark_pending_open_thread_id = threadId;
      this.spark_view = 'Chat';
      window.event_bus.$emit('SPARK-OPEN-CONVERSATION', threadId);
    },
    // Every Rey conversation the caller has, most recently used first — backs the hamburger list.
    // Includes the single thread they had before multi-conversation existed.
    GetReyConversations(systemId) {
      return axios.get(this.messenger_api_base_uri + 'threads/system/' + systemId + '/ai/conversations', this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - ${systemId}`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - ${systemId}`));
          return Promise.reject(error.message);
        });
    },
    // Creates an additional Rey conversation and resolves to its thread id. Called at FIRST SEND,
    // not when "+" is clicked — see spark_draft_active. Non-destructive: unlike ResetAIThread below
    // it never touches the caller's existing conversations.
    CreateReyConversation(systemId) {
      return axios.post(this.messenger_api_base_uri + 'threads/system/' + systemId + '/ai/conversations', {}, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - ${systemId}`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - ${systemId}`));
          return Promise.reject(error.message);
        });
    },
    // Breaks one delivered scheduled result out into its own conversation, seeded with that result.
    //
    // The server does the whole thing — create, name, seed — because the seed is a message from Rey and
    // the send endpoint refuses any message whose sender is not the caller. So there is nothing to
    // compose here; this posts the two ids and gets a thread back.
    //
    // A 409 means the Recent cap is in the way, and the caller is expected to have made room first
    // (MessengerWindow.ensureRecentCapRoom) — it is rejected like any other failure rather than
    // handled here, because only the component can ask the user to give up a conversation.
    ForkScheduledResult(systemId, threadId, messageId) {
      return axios.post(this.messenger_api_base_uri + 'threads/system/' + systemId + '/ai/conversations/fork',
        { threadId, messageId }, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - ${threadId}`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - ${threadId}`));
          return Promise.reject(error.message);
        });
    },
    // Renames one Rey conversation. Resolves to the STORED name, which the server trims and caps —
    // so the caller should render what comes back rather than what it sent.
    RenameReyConversation(systemId, threadId, name) {
      return axios.patch(this.messenger_api_base_uri + 'threads/system/' + systemId + '/ai/conversations/' + encodeURIComponent(threadId), { name }, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - ${threadId}`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - ${threadId}`));
          return Promise.reject(error.message);
        });
    },
    // Pins or unpins one Rey conversation, which is what moves it between the Recent and Pinned
    // sections of the list. Sends the DESIRED state rather than a toggle so a double click, or a
    // second tab, cannot settle on the opposite of what was clicked.
    SetReyConversationPinned(systemId, threadId, pinned) {
      return axios.patch(this.messenger_api_base_uri + 'threads/system/' + systemId + '/ai/conversations/' + encodeURIComponent(threadId) + '/pinned', { pinned }, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - ${threadId}`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - ${threadId}`));
          return Promise.reject(error.message);
        });
    },
    // Clears all messages on the caller's SystemAI thread (thread id preserved) and clears
    // the user's session caches — backs the "New conversation" control. Unlike ThreadsReset
    // (internal/debug) this loads no demo conversation.
    ResetAIThread(ResetAIThreadDTO) {
      return axios.post(this.messenger_api_base_uri + 'threads/system/' + ResetAIThreadDTO + '/ai/reset', {}, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - ${ResetAIThreadDTO}`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - ${ResetAIThreadDTO}`));
          return Promise.reject(error.message);
        });
    },
    ThreadsJoin(ThreadsJoinDTO) {
      return axios.post(this.messenger_api_base_uri + 'threads/join', ThreadsJoinDTO, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - ${ThreadsJoinDTO}`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - ${ThreadsJoinDTO}`));
          return Promise.reject(error.message);
        });
    },
    GetThreadMessages(GetThreadMessagesDTO) {
      return axios.get(this.messenger_api_base_uri + 'messages/history/' + GetThreadMessagesDTO, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - ${GetThreadMessagesDTO}`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - ${GetThreadMessagesDTO}`));
          // Preserve a 404 as a structured rejection so callers can tell "this thread no longer
          // exists" apart from a transport failure. MessagesController.GetRecentMessages returns
          // NotFound() once the thread doc is gone (e.g. after an admin "Reset AI Thread"), and
          // collapsing that to error.message threw away the only signal a caller could recover on.
          // Mirrors MessageSend's { code } rejection shape. Every other status still rejects with
          // the bare message, so existing handlers are unaffected.
          if (error.response?.status === 404)
            return Promise.reject({ code: 'thread_missing', status: 404, message: error.message });
          return Promise.reject(error.message);
        });
    },
    ThreadsUpdateLastSeen(ThreadsUpdateLastSeenDTO) {
      return axios.post(this.messenger_api_base_uri + 'threads/updateLastSeen', ThreadsUpdateLastSeenDTO, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - ${ThreadsUpdateLastSeenDTO}`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - ${ThreadsUpdateLastSeenDTO}`));
          return Promise.reject(error.message);
        });
    },
    MessageTypingStart(MessageTypingStartDTO) {
      return axios.post(this.messenger_api_base_uri + 'typing/start', MessageTypingStartDTO, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - ${MessageTypingStartDTO}`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - ${MessageTypingStartDTO}`));
          return Promise.reject(error.message);
        });
    },
    MessageTypingStop(MessageTypingStopDTO) {
      return axios.post(this.messenger_api_base_uri + 'typing/stop', MessageTypingStopDTO, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - ${MessageTypingStopDTO}`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - ${MessageTypingStopDTO}`));
          return Promise.reject(error.message);
        });
    },
    // ── Rey session lifecycle (WS-3) ─────────────────────────────────────────

    // Start (or adopt) a Rey session for this iframe.
    // In multi-iframe hosts (AutoVision embeds spark + messages), all iframes share one logical
    // session. The first iframe to mount creates the record and becomes "owner"; subsequent iframes
    // adopt the existing session_id as "guests". Only owners emit rey_session_started / _ended.
    startReySession() {
      // End any existing session before starting a new one
      if (this.rey_session_id)
        this.endReySession();

      // Use findEarliestFreshSession (no exclude) so ADOPT and race-mitigation agree on which
      // fresh session is canonical. Both use "earliest started_at wins" — deterministic.
      const freshFinder = window.Analytics?.findEarliestFreshSession(FRESH_SESSION_THRESHOLD_MS) ?? Promise.resolve(null);
      return freshFinder.then(existing => {
        if (existing) {
          // ADOPT — another iframe already owns a live session. Take its session_id.
          this.rey_session_id          = existing.session_id;
          this.rey_session_started_at  = existing.started_at;
          this.rey_action_count        = existing.action_count ?? 0;
          this.rey_is_session_owner    = false;

          // Align timeout to the original session's start so all frames end at the same wall clock.
          const remaining = Math.max(0, REY_SESSION_TIMEOUT_MS - (Date.now() - existing.started_at));
          this.rey_session_timeout_handle = setTimeout(() => {
            this.endReySession('timeout');
          }, remaining);
          return;
        }

        // CREATE — no sibling session exists. We become the owner (pending race check).
        const sessionId = crypto.randomUUID();
        this.rey_session_id         = sessionId;
        this.rey_session_started_at = Date.now();
        this.rey_action_count       = 0;
        this.rey_is_session_owner   = true;

        this.rey_session_timeout_handle = setTimeout(() => {
          this.endReySession('timeout');
        }, REY_SESSION_TIMEOUT_MS);

        // Persist FIRST so a simultaneously-loading sibling can see us in their race check.
        // rey_session_started is NOT enqueued yet — if we lose the race below we must not
        // emit an orphaned started event with no matching ended.
        window.Analytics?.persistSessionState({ session_id: sessionId, started_at: this.rey_session_started_at, action_count: 0, last_active_at: this.rey_session_started_at });

        // Race mitigation: two iframes loading simultaneously may both see no record and both
        // create. After persisting, re-check via findEarliestFreshSession (excluding our own id) —
        // if a sibling created a record with an earlier started_at, it wins. We demote to guest
        // and adopt theirs. rey_session_started is only emitted by the winning frame.
        return (window.Analytics?.findEarliestFreshSession(FRESH_SESSION_THRESHOLD_MS, sessionId) ?? Promise.resolve(null))
          .then(winner => {
            // Lose condition: sibling's started_at is earlier, OR exactly equal AND their
            // session_id sorts lexicographically first. The tie-break makes the sub-millisecond
            // collision case fully deterministic — exactly one frame in any pair demotes.
            const lost = winner && (
              winner.started_at < this.rey_session_started_at ||
              (winner.started_at === this.rey_session_started_at && winner.session_id < sessionId)
            );
            if (lost) {
              // Sibling won — discard our just-created record and adopt theirs. Do NOT enqueue
              // rey_session_started — the sibling's session is authoritative and their own
              // started event already fired (or will fire, if the sibling also won its race).
              window.Analytics?.clearSessionState(sessionId);
              this.rey_session_id = winner.session_id;
              this.rey_session_started_at = winner.started_at;
              this.rey_action_count = winner.action_count ?? 0;
              this.rey_is_session_owner = false;
              if (this.rey_session_timeout_handle) {
                clearTimeout(this.rey_session_timeout_handle);
                const remaining = Math.max(0, REY_SESSION_TIMEOUT_MS - (Date.now() - winner.started_at));
                this.rey_session_timeout_handle = setTimeout(() => {
                  this.endReySession('timeout');
                }, remaining);
              }
              return;
            }
            // Won the race (or no race happened) — enqueue rey_session_started now.
            // The Analytics module merges user_id, dealership_id, platform, surface, app_version.
            window.Analytics?.enqueue('rey_session_started', { session_id: sessionId });
          });
      });
    },

    // chatOutcome: 'user_closed' (normal teardown), 'timeout' (3-hour timer), 'abandoned' (recovery on next open)
    // Owner frame: emits rey_session_ended and clears the IndexedDB record.
    // Guest frame: just clears local Pinia state — the owner is responsible for the lifecycle event.
    endReySession(chatOutcome = 'user_closed') {
      if (!this.rey_session_id) return;

      // WS-5: close active AI chat before ending session (covers timeout and rollover cases).
      // Fires for both owner and guest — each frame may have its own local AI chat thread.
      this.closeAiChat(chatOutcome);

      if (this.rey_is_session_owner) {
        // Snapshot all values before clearing local state
        const sessionId    = this.rey_session_id;
        const started_at   = this.rey_session_started_at;
        const local_count  = this.rey_action_count;
        const duration_sec = Math.floor((Date.now() - started_at) / 1000);

        if (chatOutcome === 'timeout') {
          // 3-hour idle timeout — no page unload imminent, so the async IDB read is safe.
          // Read for the authoritative cross-frame action_count (covers the case where this
          // owner frame was idle while guest frames accumulated actions).
          (window.Analytics?.getSessionState(sessionId) ?? Promise.resolve(null)).then(record => {
            const action_count = record?.action_count ?? local_count;
            window.Analytics?.enqueue('rey_session_ended', { session_id: sessionId, duration_sec, action_count, outcome: chatOutcome });
            window.Analytics?.clearSessionState(sessionId);
          });
        } else {
          // Page-unload path (pagehide → user_closed, or component beforeUnmount). Use the
          // synchronous slot so the event is ready before Analytics.shutdown() reads the queue.
          // enqueue() is async (3 IDB awaits) and races the shutdown() flush — enqueueForShutdown()
          // writes to memory synchronously so _doFlush always sees it. Local count is reliably fresh:
          // visibilitychange → hidden fires before pagehide and touchReySession synced it.
          window.Analytics?.enqueueForShutdown('rey_session_ended', { session_id: sessionId, duration_sec, action_count: local_count, outcome: chatOutcome });
          this._markReySessionEnded(sessionId);
          window.Analytics?.clearSessionState(sessionId);
        }
      }

      if (this.rey_session_timeout_handle) {
        clearTimeout(this.rey_session_timeout_handle);
        this.rey_session_timeout_handle = null;
      }

      this.rey_session_id = null;
      this.rey_session_started_at = null;
      this.rey_action_count = 0;
      this.rey_is_session_owner = false;
    },

    // Atomically increment the shared action_count across all iframes via IndexedDB.
    // Sibling frames (owner + guests) all contribute to the same counter so rey_session_ended
    // reports the correct total. A null resolve means the owner already cleared the record —
    // this frame is a stranded guest, clean up without emitting rey_session_ended.
    // A rejection is a transient IDB failure — preserve local state; don't mistake it for a
    // deleted record.
    incrementReyActionCount() {
      if (!this.rey_session_id) return;
      if (!window.Analytics) return;
      const self = this;
      window.Analytics.incrementSessionActionCount(self.rey_session_id)
        .then(record => {
          if (!record) { self._clearLocalReySessionState(); return; }
          self.rey_action_count = record.action_count;
        })
        .catch(err => {
          console.warn('[Rey Session] Transient incrementSessionActionCount failure — keeping local state:', err);
        });
    },

    // Atomically update only last_active_at (preserves the cross-frame action_count).
    // Resolves to null → owner cleared the record, self-clean. Rejection → transient IDB
    // failure, keep local state intact (see incrementReyActionCount).
    touchReySession() {
      if (!this.rey_session_id) return;
      if (!window.Analytics) return;
      const self = this;
      window.Analytics.touchSessionStateIfExists(self.rey_session_id)
        .then(record => {
          if (!record) { self._clearLocalReySessionState(); return; }
          self.rey_action_count = record.action_count;
        })
        .catch(err => {
          console.warn('[Rey Session] Transient touchSessionStateIfExists failure — keeping local state:', err);
        });
    },

    // Clear per-frame Rey session state without emitting rey_session_ended. Used by guest frames
    // that discover the owner has already ended the session (IndexedDB record is gone).
    _clearLocalReySessionState() {
      if (this.rey_session_timeout_handle) {
        clearTimeout(this.rey_session_timeout_handle);
        this.rey_session_timeout_handle = null;
      }
      this.rey_session_id = null;
      this.rey_session_started_at = null;
      this.rey_action_count = 0;
      this.rey_is_session_owner = false;
    },

    // Write a synchronous localStorage marker recording that this session was cleanly ended.
    // localStorage is synchronous and durable across page loads (unlike IDB), so it's safe to
    // rely on during pagehide. _recoverAbandonedSession consults this on next load to skip
    // sessions that already emitted rey_session_ended via the keepalive path.
    _markReySessionEnded(sessionId) {
      try {
        const raw = window.localStorage.getItem(REY_ENDED_MARKER_KEY);
        const now = Date.now();
        const map = raw ? JSON.parse(raw) : {};
        for (const key of Object.keys(map)) {
          if (now - (map[key] || 0) > REY_ENDED_MARKER_TTL_MS) delete map[key];
        }
        map[sessionId] = now;
        window.localStorage.setItem(REY_ENDED_MARKER_KEY, JSON.stringify(map));
      } catch (_) { /* localStorage may be disabled/full — best effort */ }
    },

    // Read the cleanly-ended marker set. Returns a Set of session_ids that were marked
    // within the TTL. Recovery uses this to skip sessions the user already closed cleanly
    // but whose IDB session_state record survived (because clearSessionState didn't commit).
    _readReyEndedMarkers() {
      try {
        const raw = window.localStorage.getItem(REY_ENDED_MARKER_KEY);
        if (!raw) return new Set();
        const map = JSON.parse(raw);
        const now = Date.now();
        const fresh = new Set();
        for (const key of Object.keys(map)) {
          if (now - (map[key] || 0) <= REY_ENDED_MARKER_TTL_MS) fresh.add(key);
        }
        return fresh;
      } catch (_) {
        return new Set();
      }
    },

    // ── WS-5: AI chat conversation lifecycle ─────────────────────────────────

    openAiChat(threadId) {
      this.ai_chat_thread_id = threadId;
      this.ai_chat_started_at = Date.now();
      this.ai_chat_message_count = 0;
    },

    closeAiChat(outcome = 'user_closed') {
      if (!this.ai_chat_thread_id || !this.ai_chat_started_at || !this.rey_session_id) return;
      window.Analytics?.enqueue('chat_conversation_ended', {
        session_id: this.rey_session_id,
        conversation_id: this.ai_chat_thread_id,
        duration_sec: Math.floor((Date.now() - this.ai_chat_started_at) / 1000),
        message_count: this.ai_chat_message_count,
        outcome,
      });
      this.ai_chat_thread_id = null;
      this.ai_chat_started_at = null;
      this.ai_chat_message_count = 0;
    },

    incrementAiChatMessageCount() {
      if (this.ai_chat_thread_id)
        this.ai_chat_message_count++;
    },

    // ─────────────────────────────────────────────────────────────────────────

    MessageSend(MessageSendDTO) {
      this.incrementReyActionCount();
      return axios.post(this.messenger_api_base_uri + 'messages/send', MessageSendDTO, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - ${MessageSendDTO}`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          // The backend rejects sends targeting a closed TAC chat with HTTP 403 and a sentinel
          // error string ("This TAC chat is closed."). Surface this case as a structured object
          // so callers can distinguish it from generic failures and resync their local isClosed.
          const status = error?.response?.status;
          const serverError = error?.response?.data?.error;
          if (status === 403 && serverError === 'This TAC chat is closed.') {
            return Promise.reject({
              code: 'thread_closed',
              threadId: MessageSendDTO?.threadId,
              message: serverError,
            });
          }
          console.error(new Error(`${error.message} - ${MessageSendDTO}`));
          return Promise.reject(error.message);
        });
    },
    MessageStop(threadId) {
      return axios.post(this.messenger_api_base_uri + 'messages/stop?threadId=' + encodeURIComponent(threadId), null, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - threadId: ${threadId}`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - threadId: ${threadId}`));
          return Promise.reject(error.message);
        });
    },
    MessagePublish(MessagePublishDTO) {
      return axios.post(this.messenger_api_base_uri + 'messages/publish', MessagePublishDTO, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - ${MessagePublishDTO}`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - ${MessagePublishDTO}`));
          return Promise.reject(error.message);
        });
    },
    CreateChannel(CreateChannelDTO) {
      return axios.post(this.messenger_api_base_uri + 'channels/create', CreateChannelDTO, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - ${CreateChannelDTO}`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - ${CreateChannelDTO}`));
          return Promise.reject(error.message);
        });
    },
    InviteToChannel(InviteToChannelDTO) {
      return axios.post(this.messenger_api_base_uri + 'channels/invite', InviteToChannelDTO, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          return Promise.reject(error.message);
        });
    },
    InviteToThread(InviteToThreadDTO) {
      // Object/vehicle threads are not channels — invites go through threads/invite
      // (InviteToGroupThread server-side), which rejects channel threads. Mirrors InviteToChannel.
      return axios.post(this.messenger_api_base_uri + 'threads/invite', InviteToThreadDTO, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          return Promise.reject(error.message);
        });
    },
    GetTacTeams(dealerId) {
      // lang drives the server-composed supportHours label. Sent from here rather than resolved
      // server-side because UserContext carries no language on this path.
      const params = new URLSearchParams();
      if (dealerId) params.set('dealerId', dealerId);
      params.set('lang', this.language);
      const qs = `?${params.toString()}`;
      return axios.get(this.messenger_api_base_uri + 'messaging/tac/teams' + qs, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - GetTacTeams`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - GetTacTeams`));
          return Promise.reject(error.message);
        });
    },
    StartTacChatFromMessaging(StartTacChatDTO) {
      return axios.post(this.messenger_api_base_uri + 'messaging/tac/start', StartTacChatDTO, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - StartTacChatFromMessaging`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          // The backend returns a structured 403 ({ error, code: 'support_closed', supportHours,
          // phone }) when support is outside staffed hours; prefer that human-readable message over
          // the generic axios "Request failed..." text so the picker can surface the real reason.
          const serverError = error.response?.data?.error || error.message;
          console.error(new Error(`${serverError} - StartTacChatFromMessaging`));
          return Promise.reject(serverError);
        });
    },
    CreateGroupThread(CreateGroupThreadDTO) {
      return axios.post(this.messenger_api_base_uri + 'threads/startGroupThread', CreateGroupThreadDTO, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - ${CreateGroupThreadDTO}`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - ${CreateGroupThreadDTO}`));
          return Promise.reject(error.message);
        });
    },
    JoinOrCreateVehicleThread(JoinVehicleThreadDTO) {
      return axios.post(this.messenger_api_base_uri + 'threads/user/joinOrCreateVehicleThread', JoinVehicleThreadDTO, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - JoinOrCreateVehicleThread`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - JoinOrCreateVehicleThread`));
          return Promise.reject(error.message);
        });
    },
    DeleteThread(threadId) {
      return axios.delete(this.messenger_api_base_uri + 'threads/' + encodeURIComponent(threadId), this.auth_request_headers_dto)
        .then(response => {
          return response.data;
        })
        .catch(error => {
          return Promise.reject(error.message);
        });
    },
    LeaveThread(threadId) {
      return axios.post(this.messenger_api_base_uri + 'threads/' + encodeURIComponent(threadId) + '/leave', {}, this.auth_request_headers_dto)
        .then(response => {
          return response.data;
        })
        .catch(error => {
          return Promise.reject(error.message);
        });
    },
    RemoveParticipant(threadId, userId) {
      // Owner/enterprise-admin removal of another participant. Distinct from LeaveThread (self-leave):
      // the target user id lives in the route, and the server authorizes the caller as owner-or-admin.
      return axios.post(this.messenger_api_base_uri + 'threads/' + encodeURIComponent(threadId) + '/participants/' + encodeURIComponent(userId) + '/remove', {}, this.auth_request_headers_dto)
        .then(response => {
          return response.data;
        })
        .catch(error => {
          return Promise.reject(error.message);
        });
    },
    ObjectThread(ObjectThreadDTO) {
      return axios.get(this.messenger_api_base_uri + 'threads/' + ObjectThreadDTO, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - ${ObjectThreadDTO}`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - ${ObjectThreadDTO}`));
          return Promise.reject(error.message);
        });
    },
    BackplaneUserStatus(BackplaneUserStatusDTO) {
      return axios.get(this.messenger_api_base_uri + 'backplane/user/status/', this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - ${BackplaneUserStatusDTO}`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - ${BackplaneUserStatusDTO}`));
          return Promise.reject(error.message);
        });
    },
    fetchEnterpriseUsers(dealerGuid) {
      const apiUrl = `backplane/dealer/${dealerGuid}/users`;
      return axios.get(this.messenger_api_base_uri + apiUrl, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - ${dealerGuid}`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          // [FUS-548] Log the message only — the raw axios error carries config.headers.Authorization.
          console.error('API error:', error?.message ?? error);
          return Promise.reject(error);
        });
    },
    SubmitFeedback(SubmitFeedbackDTO) {
      return axios.post(this.messenger_api_base_uri + 'feedback/submit', SubmitFeedbackDTO, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - ${SubmitFeedbackDTO}`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - ${SubmitFeedbackDTO}`));
          return Promise.reject(error.message);
        });
    },
    GetFeedbackForConversation(conversationId) {
      return axios.get(this.messenger_api_base_uri + 'feedback/conversation/' + encodeURIComponent(conversationId), this.auth_request_headers_dto)
        .then(response => {
          if (response.data && response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - GetFeedbackForConversation`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - GetFeedbackForConversation(${conversationId})`));
          return Promise.reject(error.message);
        });
    },
    async GetSettings(GetSettingsDTO) {
      return axios.get(this.messenger_api_base_uri + 'settings/effective' + '?' + GetSettingsDTO, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - ${GetSettingsDTO}`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - ${GetSettingsDTO}`));
          return Promise.reject(error.message);
        });
    },
    async SaveSettings(SaveSettingsDTO) {
      return axios.patch(this.messenger_api_base_uri + 'settings', SaveSettingsDTO, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - SaveSettings`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - SaveSettings`));
          return Promise.reject(error.message);
        });
    },
    // Resolves whether the ReyWelcome splash should be shown for the current user right now,
    // as Promise<boolean>. The decision is the per-user "last shown" day marker
    // (Ux.WelcomeLastShownDate) read from the user-scoped settings service — server-side, so it
    // holds identically across every WingCommander host app and standalone FusionWeb (client
    // storage is partitioned per host site and can't give a cross-platform "once per day").
    // Resolved once per session and cached; later calls (re-mounts, sibling FusionBody
    // instances) return the cached decision with no network hit. Rejects when it cannot be
    // resolved (no user / settings error) so the caller can fail open.
    EnsureWelcomeGate() {
      if (this.welcome_gate_resolved)
        return Promise.resolve(this.welcome_eligible_today);

      const userId = this.user_access_info?.userId || null;
      if (!userId) return Promise.reject('EnsureWelcomeGate: no user');

      const self = this;
      const applyFrom = (settings) => {
        self.user_settings = settings;
        const lastShown = settings?.flat?.['Ux.WelcomeLastShownDate'] || null;
        self.welcome_eligible_today = lastShown !== todayLocalDateString();
        // The same comparison, recorded where MarkWelcomeShown will not clear it. See the state
        // declaration for why the landing rules cannot read welcome_eligible_today instead.
        self.rey_first_open_today = self.welcome_eligible_today;
        self.welcome_gate_resolved = true;
        return self.welcome_eligible_today;
      };

      // Reuse the settings the settings UI may already have loaded this session.
      if (this.user_settings?.flat)
        return Promise.resolve(applyFrom(this.user_settings));

      // Share a read already running rather than starting a second one — see _welcomeGateInFlight.
      if (_welcomeGateInFlight) return _welcomeGateInFlight;

      const getSettingsDTO = `tenantId=default-tenant&userId=${userId}`;
      _welcomeGateInFlight = this.GetSettings(getSettingsDTO).then(response => {
        if (response && response.flat) return applyFrom(response);
        // No resolved settings doc — treat as never shown (eligible) for this call, but leave
        // the gate unresolved so a later successful read can still correct the decision.
        self.welcome_eligible_today = true;
        // Fail open the same way: no marker read means we cannot say they have been here today, and
        // the welcome-plus-brief landing is the right guess when the answer is unknown.
        self.rey_first_open_today = true;
        return true;
      }).finally(() => { _welcomeGateInFlight = null; });

      return _welcomeGateInFlight;
    },
    // Records that the ReyWelcome splash was shown to the current user today, so it will not
    // reappear until tomorrow (browser-local date). Best-effort PATCH through the shared
    // settings service; the local cache and gate are updated immediately so re-mounts this
    // session never re-show even if the network write is slow or fails.
    MarkWelcomeShown() {
      const userId = this.user_access_info?.userId || null;
      if (!userId) return Promise.resolve();

      this.welcome_eligible_today = false;
      this.welcome_gate_resolved = true;

      const self = this;
      const saveSettingsDTO = {
        scope: {
          tenantId: 'default-tenant',
          dealerId: null,
          roleCode: null,
          userId: userId
        },
        surface: null,
        patch: { 'Ux.WelcomeLastShownDate': todayLocalDateString() }
      };
      return this.SaveSettings(saveSettingsDTO).then(response => {
        if (response && response.flat) self.user_settings = response;
      }, () => { /* best-effort — local gate already updated so it won't re-show this session */ });
    },
    // Resolves the thread id of the last Rey conversation this user had open, as Promise<string|null>.
    // Backs the "reopen where I left off" half of the startup landing rules.
    //
    // Same shape and same storage as EnsureWelcomeGate above, for the same reason: this has to hold
    // across WingCommander hosts and standalone FusionWeb, and iframe client storage is partitioned
    // per host site so it cannot. Resolved once per session and cached; a settings doc the welcome
    // gate already loaded is reused rather than re-fetched, so the common path costs no request at
    // all. Never rejects — "I don't know where they were" is an ordinary answer here (a first-ever
    // open, a settings outage) and the caller's fallback handles it.
    EnsureReyLastThread() {
      if (this.rey_last_thread_resolved)
        return Promise.resolve(this.rey_last_thread_id);

      const userId = this.user_access_info?.userId || null;
      if (!userId) return Promise.resolve(null);

      const self = this;
      const applyFrom = (settings) => {
        self.user_settings = settings;
        self.rey_last_thread_id = settings?.flat?.['Ux.ReyLastThreadId'] || null;
        self.rey_last_thread_resolved = true;
        return self.rey_last_thread_id;
      };

      if (this.user_settings?.flat)
        return Promise.resolve(applyFrom(this.user_settings));

      const getSettingsDTO = `tenantId=default-tenant&userId=${userId}`;
      return this.GetSettings(getSettingsDTO).then(response => {
        if (response && response.flat) return applyFrom(response);
        // No resolved settings doc. Leave the flag unresolved so a later successful read can still
        // answer, and land this open on the fallback.
        return null;
      }, () => null);
    },
    // Records the Rey conversation the user is now in, so the next open of the day returns to it.
    // Includes a New Topic draft, recorded as its zero-GUID placeholder — see rey_last_thread_id.
    //
    // Best-effort PATCH, exactly like MarkWelcomeShown: the local copy is updated first so the rest
    // of the session reads the right value even if the write is slow or fails, and a failure is
    // swallowed because landing on the wrong thread tomorrow is not worth an error today.
    //
    // No-ops when the id has not actually changed. This is called from the selected_thread watcher,
    // which fires on every open including re-entering the thread already remembered, and a settings
    // PATCH per thread open is not a cost worth paying to write the same value back.
    RememberReyLastThread(threadId) {
      if (!threadId || threadId === this.rey_last_thread_id) return Promise.resolve();

      const userId = this.user_access_info?.userId || null;
      if (!userId) return Promise.resolve();

      this.rey_last_thread_id = threadId;
      this.rey_last_thread_resolved = true;

      const self = this;
      const saveSettingsDTO = {
        scope: {
          tenantId: 'default-tenant',
          dealerId: null,
          roleCode: null,
          userId: userId
        },
        surface: null,
        patch: { 'Ux.ReyLastThreadId': threadId }
      };
      return this.SaveSettings(saveSettingsDTO).then(response => {
        if (response && response.flat) self.user_settings = response;
      }, () => { /* best-effort — the local value is already updated for this session */ });
    },
    UpdateContext(UpdateContextDTO) {
      let threadId = UpdateContextDTO?.threadId || null;
      let updateContextDTO = UpdateContextDTO?.payload || null;
      if (threadId === null || updateContextDTO === null) return Promise.reject();

      return axios.post(this.messenger_api_base_uri + 'threads/system/thread/' + threadId + '/updateContext', updateContextDTO, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - ${UpdateContextDTO}`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - ${UpdateContextDTO}`));
          return Promise.reject(error.message);
        });
    },
    captureLog(message, type = 'log') {
      this.debug_logs.push({
        id: Date.now() + Math.random(),
        timestamp: new Date().toISOString(),
        type,
        message: typeof message === 'object' ? (message?.message ?? String(message)) : String(message),
      });
      console[type]?.(message);
    },
    Authorize(AuthorizeDTO) {
      return axios.post('/api/authorize', AuthorizeDTO)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            this.captureLog(`${errorMsg} - Authorize`, 'error');
            return Promise.reject(errorMsg);
          }
          this.captureLog('Authorize response: ' + JSON.stringify(redactSensitiveFields(response.data)), 'log');
          return response.data;
        })
        .catch(error => {
          this.captureLog(`${error.message} - Authorize`, 'error');
          return Promise.reject(error.message);
        });
    },
    GetUserAccessInfo() {
      // The CIF rides along purely so the backend can narrow login-time provisioning for a user it
      // has no record of yet: with it, one lookup; without it, one call per onboarded enterprise
      // (76 in production). It does not change the response for a user who already exists, and the
      // parameter is optional — hosts that do not know their dealership simply omit it.
      const cif = this.current_cif;
      const url = cif ? `/api/user/access?cif=${encodeURIComponent(cif)}` : '/api/user/access';
      return axios.get(url)
        .then(response => {
          if (response.data.error) {
            this.captureLog(`${response.data.error} - GetUserAccessInfo`, 'error');
            return Promise.reject(response.data.error);
          }
          this.captureLog('GetUserAccessInfo response: ' + JSON.stringify(redactSensitiveFields(response.data)), 'log');
          return response.data;
        })
        .catch(error => {
          this.captureLog(`${error.message} - GetUserAccessInfo`, 'error');
          return Promise.reject(error.message);
        });
    },
    fetchDealerGroups(force = false) {
      // Dealer groups for the dealership-filter picker (enterprise groups + the caller's own user
      // groups). Direct SparkApi call with the Bearer token — same pattern as the scheduled-tasks
      // endpoints. Cached after the first load; pass force=true after a management change.
      // Soft-fails to an empty list so the picker simply shows no Groups section.
      if (this.dealer_groups !== null && !force)
        return Promise.resolve(this.dealer_groups);
      return axios.get(this.messenger_api_base_uri + 'dealergroups', this.auth_request_headers_dto)
        .then(response => {
          this.dealer_groups = Array.isArray(response.data) ? response.data : [];
          return this.dealer_groups;
        })
        .catch(error => {
          this.captureLog(`${error.message} - fetchDealerGroups`, 'error');
          return this.dealer_groups || [];
        });
    },
    createDealerGroup(payload) {
      // payload: { name, scope, memberCifs } — see docs/dealer-groups-api-contract.md.
      // On success the cached list is REFRESHED, not spliced: after a failed initial load
      // dealer_groups is still null, and splicing null → [row] would make fetchDealerGroups'
      // "already loaded" guard treat a one-element cache as complete — hiding every pre-existing
      // group until reload (PR #976 review). A refresh is always truthful, and if it soft-fails
      // the cache stays null so the next fetch retries. Rejections carry the server's { error }
      // message when present.
      return axios.post(this.messenger_api_base_uri + 'dealergroups', payload, this.auth_request_headers_dto)
        .then(response => {
          return this.fetchDealerGroups(true).then(() => response.data);
        })
        .catch(error => {
          const message = error.response?.data?.error || error.message;
          this.captureLog(`${message} - createDealerGroup`, 'error');
          return Promise.reject(message);
        });
    },
    updateDealerGroup(groupId, payload) {
      // Refresh-not-splice, same reasoning as createDealerGroup (PR #976 review).
      return axios.put(this.messenger_api_base_uri + 'dealergroups/' + encodeURIComponent(groupId), payload, this.auth_request_headers_dto)
        .then(response => {
          return this.fetchDealerGroups(true).then(() => response.data);
        })
        .catch(error => {
          const message = error.response?.data?.error || error.message;
          this.captureLog(`${message} - updateDealerGroup`, 'error');
          return Promise.reject(message);
        });
    },
    deleteDealerGroup(groupId) {
      // Refresh-not-splice, same reasoning as createDealerGroup (PR #976 review).
      return axios.delete(this.messenger_api_base_uri + 'dealergroups/' + encodeURIComponent(groupId), this.auth_request_headers_dto)
        .then(() => {
          return this.fetchDealerGroups(true).then(() => undefined);
        })
        .catch(error => {
          const message = error.response?.data?.error || error.message;
          this.captureLog(`${message} - deleteDealerGroup`, 'error');
          return Promise.reject(message);
        });
    },
    GetContactUrlToken(GetContactUrlTokenDTO) {
      return axios.post('/api/get_contact_url_token', GetContactUrlTokenDTO)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - ${GetContactUrlTokenDTO}`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - ${GetContactUrlTokenDTO}`));
          return Promise.reject(error.message);
        });
    },
    ThreadsReset(ThreadsResetDTO) {
      return axios.post(this.messenger_base_uri + 'internal/debug/threads/reset', ThreadsResetDTO, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - ${ThreadsResetDTO}`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - ${ThreadsResetDTO}`));
          return Promise.reject(error.message);
        });
    },
    ThreadsDisable(ThreadsDisableDTO) {
      return axios.post(this.messenger_base_uri + 'internal/debug/variants/disable', ThreadsDisableDTO, this.auth_request_headers_dto)
        .then(response => {
          if (response.data.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - ${ThreadsDisableDTO}`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - ${ThreadsDisableDTO}`));
          return Promise.reject(error.message);
        });
    },
    UploadFile(UploadFileDTO) {
      const controller = new AbortController();
      const promise = axios.post(this.messenger_api_base_uri + 'files/upload', UploadFileDTO, {
          ...this.auth_request_headers_dto,
          signal: controller.signal,
        })
        .then(response => {
          if (response.data?.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - UploadFile`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          if (axios.isCancel(error)) return Promise.reject('Upload cancelled');
          console.error(new Error(`${error.message} - UploadFile`));
          return Promise.reject(error.message);
        });

      return { promise, abort: () => controller.abort() };
    },
    // ===== Thread attachments: promoting an ephemeral file into the store's knowledge =====
    // A chat attachment is ingested under the thread with a 24h TTL, so it answers questions for this
    // conversation and then disappears. These two actions are what let a user keep one: the first finds
    // what is promotable (the upload response comes from the messenger and carries no document id), the
    // second moves it into the rooftop corpus where every user of that store can retrieve it.
    GetThreadDocuments(threadId) {
      return axios.get(`${this.messenger_api_base_uri}documents/thread/${threadId}`, this.auth_request_headers_dto)
        .then(response => {
          if (response.data?.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - GetThreadDocuments`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - GetThreadDocuments`));
          return Promise.reject(error.message);
        });
    },
    // Loads the list into shared state so every attachment card reads one fetch. force=true after an
    // upload or a promotion, when the previous answer is known to be out of date.
    LoadThreadDocuments(threadId, force = false) {
      if (!threadId) return Promise.resolve([]);
      if (!force && this.thread_documents_for === threadId) return Promise.resolve(this.thread_documents);

      // Claim the thread before awaiting so concurrent cards mounting together don't each fire a request.
      this.thread_documents_for = threadId;
      return this.GetThreadDocuments(threadId).then(documents => {
        this.thread_documents = Array.isArray(documents) ? documents : [];
        return this.thread_documents;
      }, error => {
        // An optional offer: a failed read leaves no buttons rather than an error the user cannot act on.
        this.thread_documents = [];
        return Promise.reject(error);
      });
    },
    /**
     * Whether this store may attach files — asked once per CIF and cached.
     *
     * FAIL-OPEN, deliberately, and the opposite of the server's gate. A client hint that answers "off"
     * when the feature is in fact on takes a working surface away from the user and nothing reports it;
     * the scheduled-tasks panel did exactly that to multi-store users. So an unreachable or failed check
     * leaves attachments ALLOWED and the upload endpoint — which fails closed — remains the authority.
     * The only thing this changes is WHEN a refusal is shown, never WHETHER one happens.
     */
    LoadAttachmentAvailability(cif, force = false) {
      if (!cif) return Promise.resolve(null);
      if (!force && this.attachments_availability_for === cif) return Promise.resolve(this.attachments_availability);

      // Claim the CIF before awaiting: the composer asks on mount and on every store change, and two
      // requests for the same answer would race to write it.
      this.attachments_availability_for = cif;
      // The generation this reply belongs to. The claim above keys on the CIF, which cannot see a user
      // switch — same device, same store, different person — so without this a reply already in flight
      // lands after ClearUserScopedState and restores the outgoing user's verdict, and the claim then
      // short-circuits every later ask for that store.
      const generation = this.attachments_availability_generation;
      return axios.get(`${this.messenger_api_base_uri}files/availability?dealerId=${encodeURIComponent(cif)}`, this.auth_request_headers_dto)
        .then(response => {
          if (generation !== this.attachments_availability_generation) return null;
          this.attachments_availability = response.data ?? null;
          return this.attachments_availability;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - LoadAttachmentAvailability`));
          if (generation !== this.attachments_availability_generation) return null;
          // Not rejected: nothing downstream can act on it, and null reads as "allowed" by design above.
          this.attachments_availability = null;
          // The claim is released so a later ask can retry. Keeping it would cache one transient blip for
          // the rest of the session and quietly give up the early refusal; this cannot loop, because the
          // only callers are mount and a store change.
          this.attachments_availability_for = null;
          return null;
        });
    },
    // Forget the held attachment verdict and supersede any request still in flight for it. Called when
    // the answer stops being about the current caller; the per-CIF claim already covers a store change.
    InvalidateAttachmentAvailability() {
      this.attachments_availability = null;
      this.attachments_availability_for = null;
      this.attachments_availability_generation += 1;
    },
    PromoteThreadDocument(PromoteDocumentDTO) {
      const { documentId, threadId, dealerId } = PromoteDocumentDTO;
      return axios.post(`${this.messenger_api_base_uri}documents/${documentId}/promote`, { threadId, dealerId }, this.auth_request_headers_dto)
        .then(response => {
          if (response.data?.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - PromoteThreadDocument`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - PromoteThreadDocument`));
          return Promise.reject(error.message);
        });
    },
    // ===== User-confirmed actions =====
    // Rey can ask to do something that changes data, and cannot carry it out: the call is intercepted
    // before it reaches the MCP and parked server-side under an id that only ever travels inside the
    // card. These two actions are the whole approval channel, and it is authenticated — which is what
    // makes it one the agent loop cannot reach. Nothing Rey writes can resolve a decision.
    GetDecisionStatus(decisionId) {
      return axios.get(`${this.messenger_api_base_uri}decisions/${decisionId}`, this.auth_request_headers_dto)
        .then(response => {
          if (response.data?.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - GetDecisionStatus`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - GetDecisionStatus`));
          return Promise.reject(error.message);
        });
    },
    ResolveDecision(decisionId, approved) {
      return axios.post(`${this.messenger_api_base_uri}decisions/${decisionId}`, { approved }, this.auth_request_headers_dto)
        .then(response => {
          if (response.data?.errors) {
            const errorMsg = response.data.errors.join(', ');
            console.error(new Error(`${errorMsg} - ResolveDecision`));
            return Promise.reject(errorMsg);
          }
          return response.data;
        })
        .catch(error => {
          console.error(new Error(`${error.message} - ResolveDecision`));
          return Promise.reject(error.message);
        });
    },
    // ===== Theme Management =====
    setTheme(newTheme) {
      this.theme = newTheme;
      sessionStorage.setItem('fusion_theme', newTheme);
      this._applyTheme();

      // Broadcast to other windows/iframes (same origin)
      try {
        const bc = new BroadcastChannel('fusion_theme_sync');
        bc.postMessage({ theme: newTheme });
        bc.close();
      } catch (e) { /* BroadcastChannel not supported — fallback is page refresh */ }

      // Notify host content scripts (e.g. WingCommander extension) immediately.
      // FusionWeb often runs inside an extension-injected iframe; the host content
      // script runs in the TOP frame, so target window.top (which === window when
      // FusionWeb is itself the top-level page). Saves waiting on the extension's
      // sessionStorage poll (previously up to a 2s lag).
      try {
        // FUS-474: to the top window's exact origin, and only when it is an allowed host.
        const topOrigin = hostTargetOrigin('top');
        if (topOrigin)
          window.top.postMessage({ source: 'FUSION-WEB', type: 'THEME-CHANGED', data: { theme: newTheme } }, topOrigin);
      } catch (e) { /* cross-origin top unreachable — sessionStorage poll remains the fallback */ }
    },
    _applyTheme() {
      let effectiveTheme = this.theme;

      if (effectiveTheme === 'system') {
        effectiveTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark' : 'light';
      }

      // Light is the CSS default (:root), so we only set data-theme for dark
      if (effectiveTheme === 'dark') {
        document.documentElement.setAttribute('data-theme', 'dark');
      } else {
        document.documentElement.removeAttribute('data-theme');
      }
    },
    initTheme() {
      // Restore theme from sessionStorage (will be replaced by user profile value)
      const stored = sessionStorage.getItem('fusion_theme');
      if (stored) {
        this.theme = stored;
      }

      this._applyTheme();

      // Push the restored theme to the host (e.g. the WingCommander extension toolbar) immediately.
      // initTheme() runs on every FusionWeb mount; without this the host only learns the theme when
      // setTheme() fires (settings open/change), so an extension-hosted toolbar that can't share
      // sessionStorage — e.g. on FOCUS, where FusionWeb is a cross-origin iframe — stays on its
      // cached theme. On a fresh session with no stored value, applyProfileTheme() delivers the
      // saved theme after auth resolves instead.
      if (stored) {
        try {
          const topOrigin = hostTargetOrigin('top');
          if (topOrigin)
            window.top.postMessage({ source: 'FUSION-WEB', type: 'THEME-CHANGED', data: { theme: stored } }, topOrigin);
        } catch (e) { /* cross-origin top unreachable */ }
      }

      // Listen for OS theme changes (relevant when theme === 'system')
      window.matchMedia('(prefers-color-scheme: dark)')
        .addEventListener('change', () => {
          if (this.theme === 'system') {
            this._applyTheme();
          }
        });

      // Listen for theme changes from other windows/iframes (same origin)
      try {
        const bc = new BroadcastChannel('fusion_theme_sync');
        bc.onmessage = (event) => {
          if (event.data?.theme && event.data.theme !== this.theme) {
            this.theme = event.data.theme;
            sessionStorage.setItem('fusion_theme', event.data.theme);
            this._applyTheme();
          }
        };
      } catch (e) { /* BroadcastChannel not supported */ }
    },

    // Resolve the user's saved theme (Ux.Theme) from their profile settings and apply it via
    // setTheme(), which also broadcasts THEME-CHANGED to the host (e.g. the WingCommander
    // extension toolbar). Call once auth resolves — before that GetSettings has no userId.
    // Reuses already-loaded user_settings to avoid a redundant round-trip. Non-fatal on failure:
    // the sessionStorage/system theme remains in effect.
    applyProfileTheme() {
      const userId = this.user_access_info?.userId || null;
      // Requires the bearer token too: GetSettings sends Authorization from
      // auth_request_headers_dto, which is only populated once this.token is set. Some auth
      // paths reach applyProfileTheme (via getUserAccessInfo, which authenticates by cookie)
      // before the token lands — firing GetSettings then 400s ("authorization field is
      // required"). Skip until the token exists; the initTheme() broadcast / a later
      // THEME-CHANGED still delivers the theme, and the token-bearing auth path resolves it.
      if (!userId || !this.token) return Promise.resolve();

      const applySaved = (settings) => {
        const saved = settings?.flat?.['Ux.Theme'];
        if (saved) this.setTheme(saved);
      };

      if (this.user_settings?.flat) {
        applySaved(this.user_settings);
        return Promise.resolve();
      }

      const getSettingsDTO = `tenantId=default-tenant&userId=${userId}`;
      return this.GetSettings(getSettingsDTO)
        .then(response => {
          if (response?.flat) {
            this.user_settings = response;
            applySaved(response);
          }
        })
        .catch(() => { /* non-fatal — leave current theme as-is */ });
    },

    // ===== Language Management =====
    // Mirrors the theme quartet above: setLanguage/initLanguage handle the client-side switch and
    // applyProfileLanguage resolves the user's saved Ux.Language once auth is available.
    setLanguage(newLanguage) {
      // state.language keeps the PRIMARY subtag of the applied catalog ('en' for both en-US and
      // en-GB) so the existing '=== en' comparisons and the lang params sent to the server keep
      // working; languageTag carries the full user tag. The regional catalog itself lives on
      // i18n.global.locale.
      const locale = setLocale(newLanguage);
      this.language = primaryLanguage(locale);
      // Never leave languageTag null: the sync listener compares the incoming tag against it, and a
      // bare 'en' broadcast compared against null would re-apply and re-broadcast forever between
      // two mounted frames.
      this.languageTag = (typeof newLanguage === 'string' && newLanguage.includes('-'))
        ? newLanguage
        : locale;

      // Persist and broadcast the FULL tag: a remount or sibling frame must be able to restore
      // 'fr-FR' vs 'fr-CA' — the catalog choice alone would flatten that.
      const persistTag = this.languageTag;
      sessionStorage.setItem('fusion_language', persistTag);

      // Broadcast to sibling windows/iframes (same origin). FusionWeb can be mounted more than once
      // per host page (e.g. toolbar plus an embedded body), and the settings dialog only lives in
      // one of them — without this the other frame keeps rendering the previous language until it
      // remounts.
      try {
        const bc = new BroadcastChannel('fusion_language_sync');
        bc.postMessage({ language: persistTag });
        bc.close();
      } catch (e) { /* BroadcastChannel not supported — the sibling picks it up on next mount */ }
    },
    initLanguage() {
      // Restore from sessionStorage so a remount renders in the user's language immediately instead
      // of flashing English while applyProfileLanguage() waits on auth + the settings round-trip.
      // The stored value is the FULL tag ('fr-FR', 'en-GB'); a legacy bare subtag ('fr') from an
      // older build still restores — it just has no regional subtag to recover.
      const stored = sessionStorage.getItem('fusion_language');
      if (stored) {
        this.setLanguage(stored);
      } else {
        setLocale(this.language);
      }

      // Listen for language changes from other windows/iframes (same origin)
      try {
        const bc = new BroadcastChannel('fusion_language_sync');
        bc.onmessage = (event) => {
          const incomingTag = (typeof event.data?.language === 'string' && event.data.language) || null;
          // Compare full tags: en-US -> en-GB keeps the same primary subtag but switches catalog.
          if (incomingTag && incomingTag !== this.languageTag) {
            this.setLanguage(incomingTag);
          }
        };
      } catch (e) { /* BroadcastChannel not supported */ }
    },

    // Resolve the user's saved language (Ux.Language) from their profile settings and apply it via
    // setLanguage(). Call once auth resolves — before that GetSettings has no userId. Same guards as
    // applyProfileTheme(): requires both userId and token, reuses an already-loaded settings doc,
    // and is non-fatal so the sessionStorage/default language stays in effect on failure.
    //
    // Call this AFTER applyProfileTheme() has settled rather than alongside it: that populates
    // user_settings, so on a cold cache this reuses it instead of firing a second
    // settings/effective GET for the same document.
    applyProfileLanguage() {
      const userId = this.user_access_info?.userId || null;
      if (!userId || !this.token) return Promise.resolve();

      const applySaved = (settings) => {
        const saved = settings?.flat?.['Ux.Language'];
        if (saved) this.setLanguage(saved);
      };

      if (this.user_settings?.flat) {
        applySaved(this.user_settings);
        return Promise.resolve();
      }

      const getSettingsDTO = `tenantId=default-tenant&userId=${userId}`;
      return this.GetSettings(getSettingsDTO)
        .then(response => {
          if (response?.flat) {
            this.user_settings = response;
            applySaved(response);
          }
        })
        .catch(() => { /* non-fatal — leave current language as-is */ });
    },

    // ===== Analytics (WS-6) =====
    // IndexedDB-backed module (analytics.js) handles persistence, token refresh, flush, and retry.
    // initAnalytics() must be called once after auth resolves, before startReySession().

    initAnalytics() {
      if (window.Analytics?._isInitialized) return Promise.resolve();
      const platform = PLATFORM_MAP[this.wingman_config?.FusionInitType?.InitSystem] || '';
      return window.Analytics?.init({
        analyticsBaseUri: this.analytics_api_base_uri,
        sessionToken:     this.token,
        userId:           this.user_access_info?.userId || '',
        dealershipId:     this.active_dealer?.companyId || '',
        platform,
        appVersion:       this.fusion_release_version || 'unknown',
      }).then(() => this._recoverAbandonedSession())
        .catch(() => { /* analytics failures must not surface to UI */ }) ?? Promise.resolve();
    },

    _recoverAbandonedSession() {
      return (window.Analytics?.recoverAbandonedSessions() ?? Promise.resolve([]))
        .then(priors => {
          if (!priors.length) return;

          const DISCARD_AFTER_MS   = 24 * 60 * 60 * 1000;
          const cleanlyEnded       = this._readReyEndedMarkers();

          return Promise.all(priors.map(prior => {
            const effectiveClose = prior.last_active_at ?? prior.started_at;
            const age            = Date.now() - effectiveClose;

            // Use prior.id (the actual IndexedDB key) for deletion — handles legacy
            // records written with id:'current' by the pre-multi-tab schema.
            const recordKey = prior.id ?? prior.session_id;

            // Cleanly-ended marker wins: this session already emitted rey_session_ended on
            // close via the keepalive path; the IDB record survived only because
            // clearSessionState didn't commit before unload. Delete silently without a
            // duplicate 'abandoned' emission.
            if (prior.session_id && cleanlyEnded.has(prior.session_id))
              return window.Analytics?.clearSessionState(recordKey);

            // Skip fresh records — a sibling iframe is using this session and startReySession
            // will adopt it. Recovery only acts on truly abandoned sessions.
            if (age < FRESH_SESSION_THRESHOLD_MS)
              return null;

            if (age > DISCARD_AFTER_MS)
              return window.Analytics?.clearSessionState(recordKey);

            // Atomic claim: whichever iframe's transaction commits first gets the record back
            // and emits rey_session_ended. Sibling iframes' claim returns null (record already
            // gone) and skip emitting. Prevents duplicate ended events when multiple iframes
            // run recovery in parallel on load.
            return (window.Analytics?.claimAbandonedSession(recordKey) ?? Promise.resolve(null))
              .then(claimed => {
                if (!claimed) return;
                const outcome      = age > REY_SESSION_TIMEOUT_MS ? 'timeout' : 'abandoned';
                const duration_sec = Math.floor((effectiveClose - claimed.started_at) / 1000);
                return window.Analytics?.enqueue('rey_session_ended', {
                  session_id:   claimed.session_id,
                  duration_sec,
                  action_count: claimed.action_count,
                  outcome,
                });
              })
              .catch(err => {
                console.warn('[Rey Session] claimAbandonedSession failed:', err);
              });
          }));
        });
    },
  },
});
